import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  TextField,
  Autocomplete,
  FormControl,
  FormLabel,
  FormControlLabel,
  Switch,
  FormGroup,
  ButtonGroup,
  Avatar,
  Rating,
  CircularProgress,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Schedule as TimeIcon,
  ViewList as ListIcon,
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
  ContentCopy as DuplicateIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  LocationOn as LocationIcon,
  SwapVert as SwapIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Accessible as AccessibleIcon,
  ChildCare as ChildCareIcon,
  Luggage as LuggageIcon,
  VerifiedUser as VerifiedIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { useTranslation } from 'react-i18next';
import MessageButton from '../../components/Chat/MessageButton';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import LocationAutocomplete from '../../components/Common/LocationAutocomplete';
import RideDetailsModal from '../../components/RideDetailsModal';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useSocket } from '../../contexts/SocketContext';
import TranslatingText from '../../components/Common/TranslatingText';
import { getAvatarColor, getFullImageUrl } from '../../utils/helpers';
import SocialShareModal from '../../components/Common/SocialShareModal';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

interface Ride {
  id: string;
  driverId: string;
  vehicleId: string;
  originCity: {
    id: string;
    name: string;
    province: string;
  };
  destinationCity: {
    id: string;
    name: string;
    province: string;
  };
  originDetails?: string;
  destinationDetails?: string;
  departureDateTime: string;
  pricePerPerson: number;
  availableSeats: number;
  totalSeats: number;
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'PENDING_VERIFICATION';
  allowsSmoking: boolean;
  allowsPets: boolean;
  allowsLargeLuggage: boolean;
  additionalNotes?: string;
  estimatedDuration?: number;
  allowChat?: boolean;
  vehicle?: {
    make: any;
    model: any;
    year: number;
    color: string;
    licensePlate: string;
  };
  passengers?: Array<{
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    seatsBooked: number;
  }>;
  bookedSeats?: number;
  driver?: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
  };
  bookings?: Array<{
    id: string;
    seatsBooked: number;
    totalAmount: number;
    paymentMethod: string;
    paymentStatus: string;
    status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
    specialRequests?: string;
    passenger: {
      id: string;
      firstName: string;
      lastName: string;
      preferredName?: string;
      profilePicture?: string;
      passengerRating?: number;
      totalTripsAsPassenger?: number;
      phoneNumber?: string;
      bio?: string;
      languages?: string[];
    };
  }>;
}

// Custom day component for DateTimePicker with bold and larger font
const CustomPickersDay = (props: any) => {
  return (
    <PickersDay
      {...props}
      sx={{
        fontWeight: 'bold !important',
        fontSize: '1.5em !important', // 50% increase from default 1em
        ...props.sx
      }}
    />
  );
};

const MyRidesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [activeFilter, setActiveFilter] = useState<'calendar' | 'weekly' | 'monthly'>('calendar');
  const [natureFilter, setNatureFilter] = useState<'all' | 'offered' | 'bid' | 'joined' | 'requested'>('all');
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [allRides, setAllRides] = useState<any[]>([]); // Unified list
  const [rides, setRides] = useState<Ride[]>([]); // This is legacy, will use allRides instead where possible
  const [bookings, setBookings] = useState<any[]>([]);
  const [rideRequests, setRideRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedRide, setSelectedRide] = useState<Ride | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [savedRideDialogOpen, setSavedRideDialogOpen] = useState(false);
  const [selectedSavedRide, setSelectedSavedRide] = useState<any | null>(null);
  const [isEditingRide, setIsEditingRide] = useState(false);
  const [editRideData, setEditRideData] = useState<any>(null);
  const [savingRide, setSavingRide] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | null>(null);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [isEditingRequest, setIsEditingRequest] = useState(false);
  const [bidsDialogOpen, setBidsDialogOpen] = useState(false);
  const [selectedRequestBids, setSelectedRequestBids] = useState<any[]>([]);
  const [loadingBids, setLoadingBids] = useState(false);
  const [priceSortOrder, setPriceSortOrder] = useState<'asc' | 'desc' | null>('asc');
  const [ratingSortOrder, setRatingSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [cities, setCities] = useState<any[]>([]);
  const [citiesMap, setCitiesMap] = useState<Map<string, any>>(new Map());
  const [loadingCities, setLoadingCities] = useState(false);
  const [requestFormData, setRequestFormData] = useState<any>(null);
  const [shareItem, setShareItem] = useState<{ type: 'ride' | 'request'; data: any } | null>(null);
  const { openProfile } = useUserProfile();

  const handleProfileClick = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    openProfile(userId);
  };

  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const refreshSelectedRideDetails = async (rideId: string) => {
    try {
      const response = await apiService.get(`/rides/${rideId}`);
      if (response.success) {
        setSelectedRide(response.data.ride);
        // Also update allRides list to show latest seat counts/status
        setAllRides(prevRides => prevRides.map(r => r.id === rideId ? { ...r, ...response.data.ride } : r));
      }
    } catch (error) {
      console.error('Failed to refresh ride details:', error);
    }
  };

  const handleRequestAction = async (bookingId: string, action: 'approve' | 'reject') => {
    if (!selectedRide) return;
    setActionLoading(bookingId);
    try {
      const response = await apiService.put(`/bookings/${bookingId}/approve`, { action });
      if (response.success) {
        setMessage(action === 'approve' ? 'Passenger request approved successfully!' : 'Passenger request rejected.');
        setMessageType('success');
        await refreshSelectedRideDetails(selectedRide.id);
      } else {
        setMessage(response.message || 'Failed to update request status.');
        setMessageType('error');
      }
    } catch (error: any) {
      console.error('Request action error:', error);
      setMessage(error.response?.data?.message || 'Failed to update request status.');
      setMessageType('error');
    } finally {
      setActionLoading(null);
    }
  };

  // Check if user is a driver - use isDriver flag or role
  const isDriver = user?.isDriver === true || user?.role === 'DRIVER';

  const { socket } = useSocket();

  useEffect(() => {
    // Check for success message from navigation state
    if (location.state?.message) {
      setMessage(location.state.message);
      setMessageType(location.state.type || 'info');

      // Clear the state
      window.history.replaceState({}, document.title);
    }

    loadAllUnifiedRides();
    loadCitiesForRequest();
  }, [activeTab, location, activeFilter, currentWeek, calendarMonth, calendarYear, natureFilter]);

  useEffect(() => {
    if (!socket) return;

    const handleUpdate = () => {
      loadAllUnifiedRides();
    };

    socket.on('ride_created', handleUpdate);
    socket.on('ride_updated', handleUpdate);
    socket.on('ride_cancelled', handleUpdate);
    socket.on('ride_request_created', handleUpdate);
    socket.on('ride_request_updated', handleUpdate);
    socket.on('ride_request_cancelled', handleUpdate);
    socket.on('new_ride_request', handleUpdate);
    socket.on('bid_created', handleUpdate);
    socket.on('bid_updated', handleUpdate);
    socket.on('booking_created', handleUpdate);
    socket.on('booking_updated', handleUpdate);

    return () => {
      socket.off('ride_created', handleUpdate);
      socket.off('ride_updated', handleUpdate);
      socket.off('ride_cancelled', handleUpdate);
      socket.off('ride_request_created', handleUpdate);
      socket.off('ride_request_updated', handleUpdate);
      socket.off('ride_request_cancelled', handleUpdate);
      socket.off('new_ride_request', handleUpdate);
      socket.off('bid_created', handleUpdate);
      socket.off('bid_updated', handleUpdate);
      socket.off('booking_created', handleUpdate);
      socket.off('booking_updated', handleUpdate);
    };
  }, [socket]);

  useEffect(() => {
    if (selectedRide) {
      const updatedRide = allRides.find(r => r.id === selectedRide.id);
      if (updatedRide) {
        setSelectedRide(updatedRide);
      } else if (detailDialogOpen) {
        setDetailDialogOpen(false);
        setSelectedRide(null);
      }
    }
  }, [allRides]);

  useEffect(() => {
    if (selectedRequest) {
      const updatedRequest = rideRequests.find(r => r.id === selectedRequest.id);
      if (updatedRequest) {
        setSelectedRequest(updatedRequest);
      } else if (requestDialogOpen) {
        setRequestDialogOpen(false);
        setSelectedRequest(null);
      }
    }
  }, [rideRequests]);


  const loadAllUnifiedRides = async () => {
    setLoading(true);
    try {
      const promises = [];

      // Load driver rides if user is a driver
      if (isDriver) {
        promises.push(
          apiService.get('/rides/driver/my-rides').then(res => ({ type: 'driver', data: res })),
          apiService.get('/bids/driver/my-bids').then(res => ({ type: 'bid', data: res }))
        );
      }

      // Always load passenger bookings and requests (user can be both)
      promises.push(
        apiService.get('/bookings/my-bookings').then(res => ({ type: 'booking', data: res })),
        apiService.get('/requests/passenger/my-requests').then(res => ({ type: 'request', data: res }))
      );

      const results = await Promise.all(promises);
      let unified: any[] = [];

      results.forEach((res: any) => {
        if (!res.data || !res.data.success) return;

        if (res.type === 'driver') {
          const fetchedRides = res.data.data.rides || [];
          unified = [...unified, ...fetchedRides.map((r: any) => ({ ...r, rideType: 'driver' }))];
        } else if (res.type === 'bid') {
          const bids = res.data.data.bids || [];
          unified = [...unified, ...bids.map((bid: any) => ({
            id: `bid-${bid.id}`,
            rideType: 'bid',
            bidId: bid.id,
            bidStatus: bid.status,
            originCity: bid.request.originCity,
            destinationCity: bid.request.destinationCity,
            originDetails: bid.request.originDetails,
            destinationDetails: bid.request.destinationDetails,
            departureDateTime: bid.proposedDateTime || bid.request.preferredDateTime,
            pricePerPerson: bid.priceOffer,
            totalSeats: bid.request.passengerCount,
            availableSeats: bid.request.passengerCount,
            status: bid.status === 'ACCEPTED' ? 'ACTIVE' : 'PENDING',
            message: bid.message,
            passenger: bid.request.passenger,
            request: bid.request,
            createdAt: bid.createdAt,
            isBidRide: true
          }))];
        } else if (res.type === 'booking') {
          const bookingsData = res.data.data.bookings || [];
          unified = [...unified, ...bookingsData.map((b: any) => ({
            ...b.ride,
            id: `booking-${b.id}`,
            rideType: 'booking',
            bookingId: b.id,
            bookingStatus: b.status,
            departureDateTime: b.ride.departureDateTime,
            status: b.status === 'CONFIRMED' ? 'ACTIVE' : b.status,
            booking: b,
            rideId: b.rideId
          }))];
        } else if (res.type === 'request') {
          const requests = res.data.data.requests || [];
          requests.forEach((r: any) => {
            const hasAcceptedBid = r.bids && r.bids.some((bid: any) => bid.status === 'ACCEPTED');

            // Add the request itself
            unified.push({
              ...r,
              rideType: 'request',
              departureDateTime: r.preferredDateTime,
              originCity: r.originCity,
              destinationCity: r.destinationCity,
              // If it has an accepted bid, treat it as ACTIVE regardless of CLOSED status
              status: (r.status === 'OPEN' || (r.status === 'CLOSED' && hasAcceptedBid)) ? 'ACTIVE' : r.status === 'CLOSED' ? 'COMPLETED' : 'CANCELLED'
            });

            // If user is a passenger and has bids, flatten them into the list
            if (!isDriver && r.bids && r.bids.length > 0) {
              r.bids.forEach((bid: any) => {
                unified.push({
                  id: `received-bid-${bid.id}`,
                  rideType: 'received-bid',
                  bidId: bid.id,
                  bidStatus: bid.status,
                  originCity: r.originCity,
                  destinationCity: r.destinationCity,
                  originDetails: r.originDetails,
                  destinationDetails: r.destinationDetails,
                  departureDateTime: bid.proposedDateTime || r.preferredDateTime,
                  pricePerPerson: bid.priceOffer,
                  totalSeats: r.passengerCount,
                  availableSeats: r.passengerCount,
                  status: bid.status === 'ACCEPTED' ? 'ACTIVE' : 'PENDING',
                  message: bid.message,
                  driver: bid.driver,
                  request: r,
                  createdAt: bid.createdAt,
                  isBidRide: true
                });
              });
            }
          });
        }
      });

      // Load Saved Rides
      try {
        const savedResponse = await apiService.get('/saved-rides');
        if (savedResponse.success && savedResponse.data) {
          unified = [...unified, ...savedResponse.data.map((r: any) => ({
            ...r,
            id: `saved-${r.id}`,
            rideId: r.id, // original ride id
            rideType: 'saved',
            departureDateTime: r.departureDateTime,
            status: 'SAVED',
            isSaved: true
          }))];
        }
      } catch (error) {
        console.error('Error loading saved rides in MyRidesPage:', error);
      }

      // Update legacy states for backward compatibility if needed
      const driverRides = unified.filter(r => r.rideType === 'driver');
      const passengerBookings = unified.filter(r => r.rideType === 'booking').map(r => r.booking);
      const passengerRequests = unified.filter(r => r.rideType === 'request');

      setRides(driverRides);
      setBookings(passengerBookings);
      setRideRequests(passengerRequests);
      setAllRides(unified);

    } catch (error) {
      console.error('Error loading unified rides:', error);
      setMessage(t('myRides.messages.loadFailed', 'Failed to load rides'));
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const loadCitiesForRequest = async () => {
    if (cities.length > 0) return; // Already loaded

    setLoadingCities(true);
    try {
      const response = await apiService.get('/locations/cities');
      if (response.success && response.data.cities) {
        setCities(response.data.cities);

        // Province name to abbreviation mapping
        const provinceMap: { [key: string]: string } = {
          'ontario': 'on',
          'quebec': 'qc',
          'british columbia': 'bc',
          'alberta': 'ab',
          'manitoba': 'mb',
          'saskatchewan': 'sk',
          'nova scotia': 'ns',
          'new brunswick': 'nb',
          'newfoundland and labrador': 'nl',
          'prince edward island': 'pe',
          'yukon': 'yt',
          'northwest territories': 'nt',
          'nunavut': 'nu'
        };

        // Create a map: "cityname,provinceabbr" -> City object
        const map = new Map<string, any>();
        response.data.cities.forEach((city: any) => {
          const provinceLower = city.province.toLowerCase();
          const provinceAbbr = provinceMap[provinceLower] || provinceLower;
          const key1 = `${city.name.toLowerCase()},${provinceAbbr}`;
          const key2 = `${city.name.toLowerCase()},${provinceLower}`;
          map.set(key1, city);
          map.set(key2, city);
        });
        setCitiesMap(map);
      }
    } catch (error) {
      console.error('Failed to load cities:', error);
    } finally {
      setLoadingCities(false);
    }
  };

  const loadRides = async () => {
    console.log('=== Loading rides - Tab:', activeTab, 'isDriver:', isDriver);
    setLoading(true);
    try {
      if (isDriver) {
        // Load rides for drivers
        const statusMap = ['ACTIVE', 'COMPLETED', 'CANCELLED'];
        const status = statusMap[activeTab];

        const response = await apiService.get('/rides/driver/my-rides', { status });

        if (response.success) {
          let fetchedRides = response.data.rides || [];
          console.log('=== Fetched rides:', fetchedRides.length, 'Status filter:', status);
          console.log('=== Rides data:', fetchedRides);

          // For Active tab, also load driver's bids (including ACCEPTED bids)
          // ACCEPTED bids should always show in Active tab, not Completed
          if (activeTab === 0) {
            try {
              const bidsResponse = await apiService.get('/bids/driver/my-bids');
              if (bidsResponse.success && bidsResponse.data.bids) {
                const bids = bidsResponse.data.bids;
                // Filter to only show PENDING and ACCEPTED bids in Active tab
                // ACCEPTED bids should stay in Active tab, not move to Completed
                const activeBids = bids.filter((bid: any) =>
                  bid.status === 'PENDING' || bid.status === 'ACCEPTED'
                );
                // Transform bids into ride-like objects for display
                const bidRides = activeBids.map((bid: any) => ({
                  id: `bid-${bid.id}`,
                  type: 'bid',
                  bidId: bid.id,
                  bidStatus: bid.status,
                  originCity: bid.request.originCity,
                  destinationCity: bid.request.destinationCity,
                  originDetails: bid.request.originDetails,
                  destinationDetails: bid.request.destinationDetails,
                  departureDateTime: bid.proposedDateTime || bid.request.preferredDateTime,
                  pricePerPerson: bid.priceOffer,
                  totalSeats: bid.request.passengerCount,
                  availableSeats: bid.request.passengerCount,
                  status: 'ACTIVE', // Always ACTIVE for display purposes - keeps them in Active tab
                  message: bid.message,
                  passenger: bid.request.passenger,
                  request: bid.request,
                  createdAt: bid.createdAt,
                  // Mark as bid ride for styling
                  isBidRide: true
                }));
                fetchedRides = [...fetchedRides, ...bidRides];
              }
            } catch (bidsError) {
              console.error('Error loading bids:', bidsError);
              // Continue without bids if there's an error
            }
          }

          // Filter by date for Completed and Cancelled tabs (last 4 weeks)
          if (status === 'COMPLETED' || status === 'CANCELLED') {
            const fourWeeksAgo = new Date();
            fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);

            fetchedRides = fetchedRides.filter((ride: Ride) => {
              const rideDate = new Date(ride.departureDateTime);
              return rideDate >= fourWeeksAgo;
            });
          }

          console.log('=== Setting rides:', fetchedRides.length);
          setRides(fetchedRides);
        } else {
          setMessage(response.message || t('myRides.messages.loadFailed', 'Failed to load rides'));
          setMessageType('error');
        }
      } else {
        // Load bookings for passengers
        // For Active tab, get both CONFIRMED and PENDING bookings
        if (activeTab === 0) {
          const confirmedResponse = await apiService.get('/bookings/my-bookings', { status: 'CONFIRMED' });
          const pendingResponse = await apiService.get('/bookings/my-bookings', { status: 'PENDING' });

          if (confirmedResponse.success && pendingResponse.success) {
            const confirmed = confirmedResponse.data.bookings || [];
            const pending = pendingResponse.data.bookings || [];
            setBookings([...confirmed, ...pending]);
          } else {
            setMessage(t('myRides.messages.loadFailed', 'Failed to load bookings'));
            setMessageType('error');
          }
        } else {
          const statusMap = ['CONFIRMED', 'COMPLETED', 'CANCELLED'];
          const bookingStatus = statusMap[activeTab];
          const bookingsResponse = await apiService.get('/bookings/my-bookings', { status: bookingStatus });

          if (bookingsResponse.success) {
            setBookings(bookingsResponse.data.bookings || []);
          } else {
            setMessage(bookingsResponse.message || t('myRides.messages.loadFailed', 'Failed to load bookings'));
            setMessageType('error');
          }
        }

        // Load ride requests - get all and filter by status
        try {
          console.log('Fetching passenger ride requests...');
          // Get all requests without status filter first
          const requestsResponse = await apiService.get('/requests/passenger/my-requests');
          console.log('Requests API response:', requestsResponse);

          if (requestsResponse.success) {
            let allRequests = requestsResponse.data.requests || [];
            console.log('Total requests received:', allRequests.length, allRequests);
            const now = new Date();

            // Filter requests based on active tab
            if (activeTab === 0) {
              // Active tab: Show OPEN requests that haven't expired, OR CLOSED requests with accepted bids
              const filteredRequests = allRequests.filter((req: any) => {
                const expiresAt = new Date(req.expiresAt);
                const hasAcceptedBid = req.bids && req.bids.some((bid: any) => bid.status === 'ACCEPTED');
                const isOpen = req.status === 'OPEN';
                const isClosedWithBid = req.status === 'CLOSED' && hasAcceptedBid;
                const notExpired = expiresAt > now;

                return (isOpen && notExpired) || isClosedWithBid;
              });
              setRideRequests(filteredRequests);
            } else if (activeTab === 1) {
              // Completed tab: Show CLOSED requests (that don't have active accepted bids or are in the past)
              const filteredRequests = allRequests.filter((req: any) => {
                const hasAcceptedBid = req.bids && req.bids.some((bid: any) => bid.status === 'ACCEPTED');
                const departureDate = new Date(req.preferredDateTime);
                const isPast = departureDate < now;

                return req.status === 'CLOSED' && (!hasAcceptedBid || isPast);
              });
              setRideRequests(filteredRequests);
            } else {
              // Cancelled tab: Show EXPIRED requests or CANCELLED status
              const filteredRequests = allRequests.filter((req: any) => {
                const expiresAt = new Date(req.expiresAt);
                return req.status === 'EXPIRED' || (req.status === 'OPEN' && expiresAt <= now);
              });
              setRideRequests(filteredRequests);
              console.log('Cancelled tab - Loaded ride requests:', filteredRequests.length);
            }
          } else {
            console.error('Failed to load ride requests:', requestsResponse.message);
            setRideRequests([]);
          }
        } catch (error: any) {
          console.error('Load ride requests error:', error);
          console.error('Error response:', error.response?.data);
          setRideRequests([]);
        }
      }
    } catch (error: any) {
      console.error('Load data error:', error);
      setMessage(error.message || t('myRides.messages.loadFailed', 'Failed to load data'));
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handlePreviousMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  const handleToday = () => {
    const now = new Date();
    setCalendarMonth(now.getMonth());
    setCalendarYear(now.getFullYear());
    setCurrentWeek(now);
  };

  const handlePreviousWeek = () => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() - 7);
    setCurrentWeek(newWeek);
  };

  const handleNextWeek = () => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + 7);
    setCurrentWeek(newWeek);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, ride: Ride) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedRide(ride);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRide(null);
  };

  const handleViewDetails = () => {
    if (selectedRide && (selectedRide as any).rideType === 'saved') {
      const originalRide = {
        ...selectedRide,
        id: (selectedRide as any).rideId
      };
      setSelectedSavedRide(originalRide);
      setSavedRideDialogOpen(true);
    } else {
      setDetailDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDuplicateRide = () => {
    if (selectedRide) {
      // Navigate to create ride page with prefilled data
      navigate('/create-ride', {
        state: {
          duplicateFrom: {
            vehicleId: selectedRide.vehicleId,
            originCityId: selectedRide.originCity.id,
            destinationCityId: selectedRide.destinationCity.id,
            originDetails: selectedRide.originDetails,
            destinationDetails: selectedRide.destinationDetails,
            totalSeats: selectedRide.totalSeats,
            pricePerPerson: selectedRide.pricePerPerson,
            allowsSmoking: selectedRide.allowsSmoking,
            allowsPets: selectedRide.allowsPets,
            allowsLargeLuggage: selectedRide.allowsLargeLuggage,
            additionalNotes: selectedRide.additionalNotes,
            estimatedDuration: selectedRide.estimatedDuration,
          }
        }
      });
    }
    handleMenuClose();
  };

  const handleEditRide = () => {
    if (selectedRide) {
      // Navigate to create ride page with edit mode
      navigate('/create-ride', {
        state: {
          editRide: {
            id: selectedRide.id,
            vehicleId: selectedRide.vehicleId,
            originCityId: selectedRide.originCity.id,
            destinationCityId: selectedRide.destinationCity.id,
            originDetails: selectedRide.originDetails,
            destinationDetails: selectedRide.destinationDetails,
            departureDateTime: selectedRide.departureDateTime,
            totalSeats: selectedRide.totalSeats,
            pricePerPerson: selectedRide.pricePerPerson,
            allowsSmoking: selectedRide.allowsSmoking,
            allowsPets: selectedRide.allowsPets,
            allowsLargeLuggage: selectedRide.allowsLargeLuggage,
            additionalNotes: selectedRide.additionalNotes,
            estimatedDuration: selectedRide.estimatedDuration,
          }
        }
      });
    }
    handleMenuClose();
  };

  const handleCancelRide = async () => {
    if (!selectedRide) return;

    if (!window.confirm(t('myRides.messages.cancelConfirm', 'Are you sure you want to cancel this ride?'))) {
      handleMenuClose();
      return;
    }

    try {
      const response = await apiService.delete(`/rides/${selectedRide.id}`);

      if (response.success) {
        setMessage(t('myRides.messages.cancelSuccess', 'Ride cancelled successfully'));
        setMessageType('success');
        loadAllUnifiedRides();
      } else {
        setMessage(response.message || t('myRides.messages.cancelFailed', 'Failed to cancel ride'));
        setMessageType('error');
      }
    } catch (error: any) {
      console.error('Cancel ride error:', error);
      setMessage(error.message || t('myRides.messages.cancelFailed', 'Failed to cancel ride'));
      setMessageType('error');
    }

    handleMenuClose();
  };

  const handleStartEditRide = () => {
    if (!selectedRide) return;

    // Check if ride can be edited (no passengers)
    if (selectedRide.passengers && selectedRide.passengers.length > 0) {
      setMessage(t('myRides.messages.editPassengerError', 'Cannot edit ride with existing passengers'));
      setMessageType('error');
      return;
    }

    if (selectedRide.bookedSeats && selectedRide.bookedSeats > 0) {
      setMessage(t('myRides.messages.editBookedError', 'Cannot edit ride with booked seats'));
      setMessageType('error');
      return;
    }

    // Initialize edit data
    setEditRideData({
      departureDateTime: new Date(selectedRide.departureDateTime),
      pricePerPerson: selectedRide.pricePerPerson,
      totalSeats: selectedRide.totalSeats,
      originDetails: selectedRide.originDetails || '',
      destinationDetails: selectedRide.destinationDetails || '',
      allowsSmoking: selectedRide.allowsSmoking,
      allowsPets: selectedRide.allowsPets,
      allowsLargeLuggage: selectedRide.allowsLargeLuggage,
      additionalNotes: selectedRide.additionalNotes || '',
      estimatedDuration: selectedRide.estimatedDuration || 0,
    });
    setIsEditingRide(true);
  };

  const handleCancelEditRide = () => {
    setIsEditingRide(false);
    setEditRideData(null);
  };

  const handleSaveRide = async () => {
    if (!selectedRide || !editRideData) return;

    setSavingRide(true);
    try {
      const response = await apiService.put(`/rides/${selectedRide.id}`, {
        departureDateTime: editRideData.departureDateTime.toISOString(),
        pricePerPerson: parseFloat(editRideData.pricePerPerson.toString()),
        totalSeats: parseInt(editRideData.totalSeats.toString()),
        originDetails: editRideData.originDetails,
        destinationDetails: editRideData.destinationDetails,
        allowsSmoking: editRideData.allowsSmoking,
        allowsPets: editRideData.allowsPets,
        allowsLargeLuggage: editRideData.allowsLargeLuggage,
        additionalNotes: editRideData.additionalNotes,
        estimatedDuration: editRideData.estimatedDuration ? parseInt(editRideData.estimatedDuration.toString()) : null,
      });

      if (response.success) {
        setMessage(t('myRides.messages.updateSuccess', 'Ride updated successfully'));
        setMessageType('success');
        setIsEditingRide(false);
        setEditRideData(null);
        await loadAllUnifiedRides();
        // Update selectedRide with new data
        if (response.data?.ride) {
          setSelectedRide(response.data.ride);
        }
      } else {
        setMessage(response.message || t('myRides.messages.updateFailed', 'Failed to update ride'));
        setMessageType('error');
      }
    } catch (error: any) {
      console.error('Save ride error:', error);
      setMessage(error.message || t('myRides.messages.updateFailed', 'Failed to update ride'));
      setMessageType('error');
    } finally {
      setSavingRide(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'COMPLETED': return 'default';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ACTIVE': return t('myRides.tabs.active', 'Active');
      case 'COMPLETED': return t('myRides.tabs.completed', 'Completed');
      case 'CANCELLED': return t('myRides.tabs.cancelled', 'Cancelled');
      case 'SAVED': return t('myRides.tabs.saved', 'Saved');
      default: return status;
    }
  };

  const getCalendarCellColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return '#e8f5e9'; // pale green
      case 'COMPLETED': return '#e3f2fd'; // pale blue
      case 'CANCELLED': return '#ffebee'; // pale red
      default: return '#f5f5f5';
    }
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString(i18n.language),
      time: date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
    };
  };

  const renderRideCard = (ride: Ride) => {
    const { date, time } = formatDateTime(ride.departureDateTime);
    const isBidRide = (ride as any).isBidRide || (ride as any).type === 'bid';

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={ride.id}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            border: isBidRide ? '2px solid' : '1px solid',
            borderColor: isBidRide ? '#9c27b0' : 'rgba(255, 255, 255, 0.3)',
            background: isBidRide
              ? 'linear-gradient(135deg, rgba(243, 229, 245, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%)'
              : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-5px) scale(1.01)',
              boxShadow: '0 20px 50px 0 rgba(31, 38, 135, 0.2)',
              borderColor: isBidRide ? '#7b1fa2' : 'primary.main',
            },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={() => {
            if ((ride as any).rideType === 'saved') {
              const originalRide = {
                ...ride,
                id: (ride as any).rideId
              };
              setSelectedSavedRide(originalRide);
              setSavedRideDialogOpen(true);
            } else {
              setSelectedRide(ride);
              setDetailDialogOpen(true);
            }
          }}
        >
          <CardContent sx={{ flexGrow: 1, p: { xs: 2.5, md: 4.5 }, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                {isBidRide && (
                  <Chip
                    label={t('myRides.cards.bidRide', 'Bid Ride')}
                    size="small"
                    sx={{
                      mb: 1.5,
                      background: 'linear-gradient(45deg, #9c27b0 30%, #ce93d8 90%)',
                      color: 'white',
                      fontSize: '1.35rem',
                      fontWeight: 'bold',
                      px: 2,
                      py: 1,
                      height: 'auto',
                      boxShadow: '0 4.5px 7.5px 3px rgba(156, 39, 176, .3)',
                      '& .MuiChip-label': { whiteSpace: 'normal', px: 2 }
                    }}
                  />
                )}
                <Typography variant="h6" sx={{ fontWeight: '800', mb: 1, fontSize: '1.5rem', letterSpacing: '-0.5px', wordBreak: 'break-word' }}>
                  {ride.originCity.name} <span style={{ color: '#aaa' }}>→</span> {ride.destinationCity.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{
                    p: 0.5,
                    borderRadius: '50%',
                    bgcolor: 'rgba(0,0,0,0.05)',
                    display: 'flex'
                  }}>
                    <TimeIcon sx={{ fontSize: '1.8rem' }} color="action" />
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, fontSize: '1.65rem' }}>
                    {t('myRides.cards.dateTime', '{{date}} at {{time}}', { date, time })}
                  </Typography>
                </Box>
                {isBidRide && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, width: '100%' }}>
                    <Chip
                      label={t('myRides.cards.offeredPrice', 'Offered: ${{price}} CAD', { price: ride.pricePerPerson })}
                      size="medium"
                      sx={{
                        bgcolor: 'rgba(156, 39, 176, 0.1)',
                        color: '#9c27b0',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        py: 0.5,
                        px: 1,
                        height: 'auto',
                        border: '1px solid rgba(156, 39, 176, 0.2)',
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-word'
                        }
                      }}
                    />
                    {(ride as any).rideType === 'received-bid' && (ride as any).driver && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Avatar
                          sx={{ width: 24, height: 24, fontSize: '0.9rem', bgcolor: getAvatarColor((ride as any).driver.preferredName || (ride as any).driver.firstName) }}
                          src={getFullImageUrl((ride as any).driver.profilePicture)}
                        >
                          {((ride as any).driver.preferredName || (ride as any).driver.firstName)[0]}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                          {(ride as any).driver.preferredName || (ride as any).driver.firstName} (Driver)
                        </Typography>
                      </Box>
                    )}
                    {(ride as any).message && (
                      <Box sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: '16px',
                        border: '1px solid rgba(156, 39, 176, 0.2)',
                        bgcolor: 'rgba(156, 39, 176, 0.05)',
                        width: '100%',
                        maxWidth: '100%',
                        position: 'relative',
                        boxSizing: 'border-box',
                        overflow: 'hidden'
                      }}>
                        <Typography sx={{
                          fontSize: '1.1rem',
                          color: '#9c27b0',
                          fontWeight: 500,
                          lineHeight: 1.4,
                          wordBreak: 'break-word',
                          display: 'flex',
                          gap: 0.5
                        }}>
                          {t('myRides.cards.messageLabel', 'Message:')}
                          <TranslatingText
                            text={`"${(ride as any).message}"`}
                            sx={{ display: 'inline' }}
                          />
                        </Typography>
                      </Box>
                    )}
                    {(ride as any).bidStatus && (
                      <Chip
                        label={(ride as any).bidStatus}
                        size="medium"
                        color={(ride as any).bidStatus === 'ACCEPTED' ? 'success' : (ride as any).bidStatus === 'REJECTED' ? 'error' : 'warning'}
                        sx={{ fontWeight: 'bold', fontSize: '1.2rem', height: 'auto', py: 0.5 }}
                      />
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* Share only when ride still has open seats (worth promoting) */}
                  {!isBidRide && ((ride as any).availableSeats > 0 || !(ride as any).bookedSeats) && (
                    <IconButton
                      size="medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShareItem({ type: 'ride', data: ride });
                      }}
                      sx={{
                        p: 1.5,
                        bgcolor: 'rgba(33,150,243,0.1)',
                        '&:hover': { bgcolor: 'rgba(33,150,243,0.2)' },
                        '& .MuiSvgIcon-root': { fontSize: '1.8rem', color: '#2196f3' }
                      }}
                      title="Share this ride"
                    >
                      <ShareIcon />
                    </IconButton>
                  )}
                  {!isBidRide && (
                    <IconButton
                      size="medium"
                      onClick={(e) => handleMenuOpen(e, ride)}
                      sx={{
                        p: 1.5,
                        bgcolor: 'rgba(0,0,0,0.05)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
                        '& .MuiSvgIcon-root': { fontSize: '1.8rem' }
                      }}
                    >
                      <MoreIcon />
                    </IconButton>
                  )}
                </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderBookingCard = (booking: any) => {
    const ride = booking.ride;
    const { date, time } = formatDateTime(ride.departureDateTime);
    const isPending = booking.status === 'PENDING';

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={booking.id}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            border: '2px solid',
            borderColor: isPending ? '#ff9800' : '#81c784',
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-5px) scale(1.01)',
              boxShadow: '0 20px 50px 0 rgba(31, 38, 135, 0.2)',
            },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={() => navigate(`/ride/${ride.id}`)}
        >
          <CardContent sx={{ flexGrow: 1, p: { xs: 2.5, md: 4.5 }, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Chip
                  label={isPending ? t('myRides.cards.askedToJoin', "Asked to join") : t('myRides.cards.joinedRide', "Joined Ride")}
                  size="small"
                  sx={{
                    mb: 1.5,
                    background: isPending
                      ? 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)'
                      : 'linear-gradient(45deg, #66bb6a 30%, #81c784 90%)',
                    color: 'white',
                    fontSize: '1.35rem',
                    fontWeight: 'bold',
                    px: 2,
                    py: 1,
                    height: 'auto',
                    boxShadow: isPending ? '0 4.5px 7.5px 3px rgba(255, 152, 0, .3)' : '0 4.5px 7.5px 3px rgba(76, 175, 80, .3)',
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: '800', mb: 1, fontSize: '1.5rem', letterSpacing: '-0.5px', wordBreak: 'break-word' }}>
                  {ride.originCity.name} <span style={{ color: '#aaa' }}>→</span> {ride.destinationCity.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{ p: 0.75, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.05)', display: 'flex' }}>
                    <TimeIcon sx={{ fontSize: '1.8rem' }} color="action" />
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, fontSize: '1.65rem' }}>
                    {t('myRides.cards.dateTime', '{{date}} at {{time}}', { date, time })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<PersonIcon sx={{ fontSize: '1.5rem !important' }} />}
                    label={t('myRides.cards.seatsBooked', '{{count}} seat(s)', { count: booking.seatsBooked })}
                    size="medium"
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 'bold', borderRadius: '18px', fontSize: '1.2rem', py: 0.5, height: 'auto' }}
                  />
                  <Chip
                    label={`$${booking.totalAmount}`}
                    size="medium"
                    color="success"
                    variant="outlined"
                    sx={{ fontWeight: 'bold', borderRadius: '18px', fontSize: '1.2rem', py: 0.5, height: 'auto' }}
                  />
                  <Chip
                    label={booking.status}
                    size="medium"
                    color={booking.status === 'CONFIRMED' ? 'success' : 'default'}
                    sx={{ fontWeight: 'bold', fontSize: '1.2rem', py: 0.5, height: 'auto' }}
                  />
                </Box>
                {ride.driver && (
                  <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 36, height: 36, fontSize: '1.2rem' }}>
                      {(ride.driver.preferredName || ride.driver.firstName)[0]}
                    </Avatar>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500, fontSize: '1.2rem' }}>
                      {t('myRides.cards.driver', 'Driver: {{name}}', { name: `${ride.driver.preferredName || ride.driver.firstName || ''} ${ride.driver.lastName ? ride.driver.lastName.charAt(0) + '.' : ''}` })}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const handleRequestClick = async (request: any) => {
    // Check if the current user owns this request
    const isOwner =
      request.passengerId === user?.id ||
      request.passenger?.id === user?.id ||
      request.userId === user?.id;

    // If request has bids AND user is not the owner (e.g. a driver browsing), show bids dialog
    // Owners always see the Request Details dialog so they can access Edit/Delete buttons
    const hasBids = (request.totalBids > 0) || (request.bids && request.bids.length > 0);
    if (hasBids && !isDriver && !isOwner) {
      setSelectedRequest(request);
      setBidsDialogOpen(true);
      await loadRequestBids(request.id);
    } else {
      setSelectedRequest(request);
      // Initialize form data from request
      const originCity = cities.find(c => c.id === request.originCityId);
      const destinationCity = cities.find(c => c.id === request.destinationCityId);
      setRequestFormData({
        originCity,
        destinationCity,
        originDetails: request.originDetails || '',
        destinationDetails: request.destinationDetails || '',
        preferredDateTime: new Date(request.preferredDateTime),
        timeFlexibility: request.timeFlexibility || 0,
        passengerCount: request.passengerCount || 1,
        minBudget: request.minBudget?.toString() || '',
        maxBudget: request.maxBudget?.toString() || '',
        needsLargeLuggage: request.needsLargeLuggage || false,
        needsChildSeat: request.needsChildSeat || false,
        needsWheelchairAccess: request.needsWheelchairAccess || false,
        specialRequirements: request.specialRequirements || '',
        description: request.description || '',
      });
      setRequestDialogOpen(true);
      setIsEditingRequest(false);
    }
  };

  const loadRequestBids = async (requestId: string) => {
    setLoadingBids(true);
    try {
      const response = await apiService.get(`/requests/${requestId}`);
      console.log('Bids response:', response);
      if (response.success) {
        // Backend returns { success: true, data: { ...request, bids: [...] } }
        const requestData = response.data;
        if (requestData?.bids && Array.isArray(requestData.bids)) {
          setSelectedRequestBids(requestData.bids);
        } else {
          console.warn('No bids found in response:', requestData);
          setSelectedRequestBids([]);
        }
      } else {
        setMessage('Failed to load bids');
        setMessageType('error');
      }
    } catch (error: any) {
      console.error('Error loading bids:', error);
      setMessage('Failed to load bids');
      setMessageType('error');
    } finally {
      setLoadingBids(false);
    }
  };

  const handleDriverProfileClick = (driverId: string) => {
    navigate(`/driver/${driverId}`);
  };

  const handleAcceptBid = async (bidId: string) => {
    if (!selectedRequest) return;

    if (!window.confirm('Are you sure you want to accept this bid? This will close your ride request.')) {
      return;
    }

    try {
      const response = await apiService.patch(`/bids/${bidId}/accept`, {});
      if (response.success) {
        setMessage('Bid accepted successfully!');
        setMessageType('success');
        setBidsDialogOpen(false);
        setSelectedRequestBids([]);
        // Reload requests to update the list
        loadRides();
      } else {
        setMessage(response.message || 'Failed to accept bid');
        setMessageType('error');
      }
    } catch (error: any) {
      console.error('Accept bid error:', error);
      setMessage(error.response?.data?.message || 'Failed to accept bid');
      setMessageType('error');
    }
  };

  const handleEditRequest = () => {
    setIsEditingRequest(true);
  };

  const handleSaveRequest = async () => {
    if (!selectedRequest || !requestFormData) return;

    const hasBids = selectedRequest.totalBids > 0 || selectedRequest.acceptedBid;
    if (hasBids) {
      setMessage('Cannot edit request with existing bids');
      setMessageType('error');
      return;
    }

    try {
      const updatedData = {
        originCityId: requestFormData.originCity?.id,
        destinationCityId: requestFormData.destinationCity?.id,
        originDetails: requestFormData.originDetails,
        destinationDetails: requestFormData.destinationDetails,
        preferredDateTime: requestFormData.preferredDateTime.toISOString(),
        timeFlexibility: requestFormData.timeFlexibility,
        passengerCount: requestFormData.passengerCount,
        minBudget: requestFormData.minBudget ? parseFloat(requestFormData.minBudget) : null,
        maxBudget: requestFormData.maxBudget ? parseFloat(requestFormData.maxBudget) : null,
        needsLargeLuggage: requestFormData.needsLargeLuggage,
        needsChildSeat: requestFormData.needsChildSeat,
        needsWheelchairAccess: requestFormData.needsWheelchairAccess,
        specialRequirements: requestFormData.specialRequirements,
        description: requestFormData.description,
      };

      const response = await apiService.put(`/requests/${selectedRequest.id}`, updatedData);

      if (response.success) {
        setMessage('Ride request updated successfully');
        setMessageType('success');
        setRequestDialogOpen(false);
        setIsEditingRequest(false);
        loadRides();
      } else {
        setMessage(response.message || 'Failed to update ride request');
        setMessageType('error');
      }
    } catch (error: any) {
      console.error('Update request error:', error);
      setMessage(error.response?.data?.message || error.message || 'Failed to update ride request');
      setMessageType('error');
    }
  };

  const handleDeleteRequest = async () => {
    if (!selectedRequest) return;

    if (!window.confirm('Are you sure you want to delete this ride request? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.delete(`/requests/${selectedRequest.id}`);
      if (response.success) {
        setMessage('Ride request deleted successfully');
        setMessageType('success');
        setRequestDialogOpen(false);
        setSelectedRequest(null);
        loadAllUnifiedRides();
      } else {
        setMessage(response.message || 'Failed to delete ride request');
        setMessageType('error');
      }
    } catch (error: any) {
      console.error('Delete request error:', error);
      setMessage(error.response?.data?.message || error.message || 'Failed to delete ride request');
      setMessageType('error');
    }
  };

  const renderRideRequestCard = (request: any) => {
    const { date, time } = formatDateTime(request.preferredDateTime);
    const hasBids = request.totalBids > 0 || request.acceptedBid;

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={request.id}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            border: '2px solid transparent',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.8), rgba(255,255,255,0.4))',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-5px) scale(1.01)',
              boxShadow: '0 20px 50px 0 rgba(31, 38, 135, 0.2)',
              borderColor: '#2196f3',
            },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={() => handleRequestClick(request)}
        >
          <CardContent sx={{ flexGrow: 1, p: { xs: 2.5, md: 4.5 }, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Chip
                    label={t('myRides.cards.rideRequest', 'Ride Request')}
                    size="medium"
                    sx={{
                      background: 'linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.35rem',
                      px: 2,
                      py: 1,
                      height: 'auto',
                      boxShadow: '0 4.5px 7.5px 3px rgba(33, 150, 243, .3)',
                    }}
                  />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: '800', fontSize: '1.5rem', letterSpacing: '-0.5px', mb: 1, wordBreak: 'break-word' }}>
                  {request.originCity.name} <span style={{ color: '#aaa' }}>→</span> {request.destinationCity.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{ p: 0.75, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.05)', display: 'flex' }}>
                    <TimeIcon sx={{ fontSize: '1.8rem' }} color="action" />
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, fontSize: '1.65rem' }}>
                    {t('myRides.cards.preferredTime', 'Preferred: {{date}} at {{time}}', { date, time })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<PersonIcon sx={{ fontSize: '1.5rem !important' }} />}
                    label={t('myRides.cards.passengerCount', '{{count}} passenger(s)', { count: request.passengerCount })}
                    size="medium"
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 'bold', borderRadius: '18px', fontSize: '1.2rem', height: 'auto', py: 0.5 }}
                  />
                  {request.minBudget && request.maxBudget && (
                    <Chip
                      label={`$${request.minBudget} - $${request.maxBudget}`}
                      size="medium"
                      color="success"
                      variant="outlined"
                      sx={{ fontWeight: 'bold', borderRadius: '18px', fontSize: '1.2rem', height: 'auto', py: 0.5 }}
                    />
                  )}
                  {request.totalBids > 0 && (
                    <Chip
                      label={t('myRides.cards.bidCount', '{{count}} bids', { count: request.totalBids })}
                      size="medium"
                      color="warning"
                      sx={{ fontWeight: 'bold', fontSize: '1.2rem', height: 'auto', py: 0.5 }}
                    />
                  )}
                  {request.acceptedBid && (
                    <Chip
                      label={t('myRides.cards.accepted', 'Accepted')}
                      size="medium"
                      color="success"
                      sx={{ fontWeight: 'bold', fontSize: '1.2rem', height: 'auto', py: 0.5 }}
                    />
                  )}
                </Box>
              </Box>
              {/* Share only for open requests with no accepted bid yet (worth promoting) */}
              {request.status === 'OPEN' && !request.acceptedBid && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShareItem({ type: 'request', data: request });
                  }}
                  sx={{ bgcolor: 'rgba(33,150,243,0.1)', alignSelf: 'flex-start', '&:hover': { bgcolor: 'rgba(33,150,243,0.2)' } }}
                  title="Share this request"
                >
                  <ShareIcon sx={{ fontSize: '1.5rem', color: '#2196f3' }} />
                </IconButton>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const getRideColor = (item: any) => {
    if (item.rideType === 'bid' || item.rideType === 'received-bid') return '#FF9800'; // Orange
    if (item.rideType === 'booking') return '#4CAF50'; // Green
    if (item.rideType === 'request') return '#9C27B0'; // Purple
    if (item.rideType === 'saved') return '#10b981'; // Green (vibrant)
    return '#2196F3'; // Blue (driver)
  };

  const filteredRides = React.useMemo(() => {
    let list = [...allRides];

    const now = new Date();
    // 1. Filter by Status Tab (Upcoming, Completed, Cancelled)
    if (activeTab === 0) {
      list = list.filter(r => {
        const rideDate = new Date(r.departureDateTime);
        const isActive = r.status === 'ACTIVE' || r.status === 'PENDING' || r.status === 'PENDING_VERIFICATION';
        return isActive && rideDate >= now;
      });
    } else if (activeTab === 1) {
      list = list.filter(r => {
        const rideDate = new Date(r.departureDateTime);
        const hasPassengers = r.bookedSeats > 0 || (r.bookings && r.bookings.length > 0);
        return r.status === 'COMPLETED' || (rideDate < now && r.status !== 'CANCELLED' && hasPassengers);
      });
    } else if (activeTab === 2) {
      list = list.filter(r => {
        const rideDate = new Date(r.departureDateTime);
        const noPassengers = !r.bookings || r.bookings.length === 0;
        const isExpired = rideDate < now && r.status !== 'CANCELLED' && r.status !== 'COMPLETED' && noPassengers;
        return r.status === 'CANCELLED' || r.status === 'EXPIRED' || isExpired;
      });
    } else if (activeTab === 3) {
      list = list.filter(r => r.rideType === 'saved');
    }
    // 2. Filter by Date Filter (Calendar, Weekly, Monthly)
    if (activeFilter === 'weekly') {
      const startOfWeek = new Date(currentWeek);
      startOfWeek.setDate(currentWeek.getDate() - currentWeek.getDay());
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      list = list.filter(r => {
        const d = new Date(r.departureDateTime);
        return d >= startOfWeek && d <= endOfWeek;
      });
    } else if (activeFilter === 'monthly') {
      list = list.filter(r => {
        const d = new Date(r.departureDateTime);
        return d.getMonth() === calendarMonth && d.getFullYear() === calendarYear;
      });
    }

    // 3. Filter by Nature (Offered, Bid, Joined, Requested)
    if (natureFilter !== 'all') {
      list = list.filter(r => {
        if (natureFilter === 'offered') return r.rideType === 'driver';
        if (natureFilter === 'bid') {
          // Both driver-side bids and passenger-side requests with accepted bids
          const isAcceptedRequest = r.rideType === 'request' && r.bids && r.bids.some((bid: any) => bid.status === 'ACCEPTED');
          return r.rideType === 'bid' || r.rideType === 'received-bid' || isAcceptedRequest;
        }
        if (natureFilter === 'joined') return r.rideType === 'booking';
        if (natureFilter === 'requested') {
          // Passenger-side requests that DON'T have accepted bids yet (unless user specifically wants to see them here too)
          const hasAcceptedBid = r.bids && r.bids.some((bid: any) => bid.status === 'ACCEPTED');
          return r.rideType === 'request' && !hasAcceptedBid;
        }
        return true;
      });
    }

    return list.sort((a, b) => new Date(a.departureDateTime).getTime() - new Date(b.departureDateTime).getTime());
  }, [allRides, activeTab, activeFilter, currentWeek, calendarMonth, calendarYear, natureFilter]);

  const renderCalendarView = () => {
    // Group ALL rides by date for the calendar indicators
    // We filter by Tab status but not by week/month filter so the calendar shows all month data
    const listForCalendar = allRides.filter(r => {
      // 1. First filter by Status Tab
      let matchesTab = true;
      if (activeTab === 0) {
        const hasAcceptedBid = r.bids && r.bids.some((bid: any) => bid.status === 'ACCEPTED');
        matchesTab = r.status === 'ACTIVE' || r.status === 'PENDING' || r.status === 'PENDING_VERIFICATION' || (r.status === 'CLOSED' && hasAcceptedBid);
      }
      else if (activeTab === 1) {
        const hasAcceptedBid = r.bids && r.bids.some((bid: any) => bid.status === 'ACCEPTED');
        matchesTab = r.status === 'COMPLETED' || (r.status === 'CLOSED' && !hasAcceptedBid);
      }
      else if (activeTab === 2) matchesTab = r.status === 'CANCELLED' || r.status === 'EXPIRED';
      else if (activeTab === 3) matchesTab = r.rideType === 'saved';

      if (!matchesTab) return false;

      // 2. Then filter by Nature (Role-based) - The "AND" logic
      if (natureFilter !== 'all') {
        if (natureFilter === 'offered') return r.rideType === 'driver';
        if (natureFilter === 'bid') return r.rideType === 'bid' || r.rideType === 'received-bid';
        if (natureFilter === 'joined') return r.rideType === 'booking';
        if (natureFilter === 'requested') return r.rideType === 'request';
        return false;
      }

      return true;
    });

    const ridesByDate: { [key: string]: any[] } = {};
    listForCalendar.forEach(ride => {
      const dateKey = new Date(ride.departureDateTime).toDateString();
      if (!ridesByDate[dateKey]) {
        ridesByDate[dateKey] = [];
      }
      ridesByDate[dateKey].push(ride);
    });

    const MyRidesCalendarDay = (props: any) => {
      const { day, ...other } = props;
      const dateKey = day.toDateString();
      const dayRides = ridesByDate[dateKey] || [];

      return (
        <Box sx={{ position: 'relative' }}>
          <PickersDay
            {...other}
            day={day}
            sx={{
              fontWeight: '900 !important',
              zIndex: 2,
              position: 'relative',
              ...(dayRides.length > 0 && {
                border: '2px solid',
                borderColor: '#2196f3', // Blue circle around the date
              }),
            }}
          />
          {dayRides.length > 0 && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}>
              {dayRides.slice(0, 4).map((ride, idx) => {
                const total = Math.min(dayRides.length, 4);
                const angle = (idx / total) * 2 * Math.PI - Math.PI / 2;
                const radius = 13; // Set radius to 13 to keep dots INSIDE the blue circle
                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;

                return (
                  <Box
                    key={ride.id}
                    sx={{
                      position: 'absolute',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: getRideColor(ride),
                      opacity: 0.9,
                      border: '1.5px solid white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      transform: `translate(${x}px, ${y}px)`,
                      zIndex: 1
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      );
    };

    // Get rides for the selected date to display in the side list
    const selectedDateRides = selectedCalendarDate
      ? listForCalendar.filter(r => new Date(r.departureDateTime).toDateString() === selectedCalendarDate.toDateString())
      : [];

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{
          display: 'flex',
          gap: { xs: 4, md: 6 },
          flexDirection: { xs: 'column', md: 'row' },
          mt: 4,
          alignItems: 'flex-start'
        }}>
          {/* Calendar Side - Matches Search Results Style */}
          <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '550px' } }}>
            <Paper sx={{
              p: 4,
              borderRadius: '32px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.08)',
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              overflow: 'hidden',
              minHeight: '520px'
            }}>
              <Box sx={{
                transform: 'scale(1.5)',
                transformOrigin: 'top left',
                width: '66.66%',
                '& *': { fontWeight: '700 !important' },
                '& .MuiPickersDay-root': {
                  fontSize: '1rem',
                  '&.Mui-selected': {
                    backgroundColor: '#2196f3 !important',
                  }
                },
                '& .MuiDayCalendar-weekDayLabel': {
                  fontWeight: '900',
                  color: '#2196f3',
                  fontSize: '0.9rem'
                },
                '& .MuiPickersCalendarHeader-label': {
                  fontSize: '1.1rem',
                  color: '#1e293b'
                }
              }}>
                <DateCalendar
                  value={selectedCalendarDate}
                  onChange={(date) => setSelectedCalendarDate(date)}
                  slots={{
                    day: MyRidesCalendarDay,
                  }}
                />
              </Box>

              {/* The legend has been moved to the status header area for better visibility */}
            </Paper>
          </Box>

          {/* Result Cards Side */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {selectedCalendarDate ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                  <TodayIcon sx={{ color: '#2196f3', fontSize: '2.5rem' }} />
                  <Typography variant="h4" sx={{ fontWeight: '900', color: '#1e293b', letterSpacing: '-0.02em' }}>
                    {selectedCalendarDate.toLocaleDateString(i18n.language, { weekday: 'long', month: 'long', day: 'numeric' })}
                  </Typography>
                </Box>

                {selectedDateRides.length > 0 ? (
                  <Grid container spacing={3}>
                    {selectedDateRides.map(ride => {
                      if (ride.rideType === 'request') return renderRideRequestCard(ride);
                      if (ride.rideType === 'booking') return renderBookingCard(ride.booking);
                      return renderRideCard(ride);
                    })}
                  </Grid>
                ) : (
                  <Paper sx={{
                    p: 10,
                    textAlign: 'center',
                    borderRadius: '32px',
                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                    border: '2px dashed rgba(0,0,0,0.08)',
                    boxShadow: 'none'
                  }}>
                    <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 600 }}>
                      {t('myRides.messages.noItemsOnDate', 'No rides or requests on this date')}
                    </Typography>
                  </Paper>
                )}
              </Box>
            ) : (
              <Paper sx={{
                p: 12,
                textAlign: 'center',
                borderRadius: '32px',
                bgcolor: 'rgba(255, 255, 255, 0.5)',
                border: '2px dashed rgba(0,0,0,0.08)',
                boxShadow: 'none'
              }}>
                <CalendarIcon sx={{ fontSize: 100, color: 'rgba(0,0,0,0.05)', mb: 3 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#94a3b8', letterSpacing: '-0.01em' }}>
                  {t('myRides.messages.selectDate', 'Select a date on the calendar to see details')}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      </LocalizationProvider>
    );
  };

  const renderDetailDialog = () => {
    if (!selectedRide) return null;

    const { date, time } = formatDateTime(selectedRide.departureDateTime);
    const isBidRide = (selectedRide as any).isBidRide || (selectedRide as any).type === 'bid';
    const hasBookings = (selectedRide.passengers && selectedRide.passengers.length > 0) || (selectedRide.bookedSeats && selectedRide.bookedSeats > 0);
    const isAcceptedBid = isBidRide && (selectedRide as any).bidStatus === 'ACCEPTED';
    const isBookingRide = (selectedRide as any).rideType === 'booking';
    const cleanContextType = isBookingRide ? 'ride' : isBidRide ? 'bid' : 'ride';
    const cleanContextId = isBookingRide ? (selectedRide as any).rideId : isBidRide ? (selectedRide as any).bidId : selectedRide.id;

    return (
      <Dialog
        open={detailDialogOpen}
        onClose={() => {
          if (!isEditingRide) {
            setDetailDialogOpen(false);
            setIsEditingRide(false);
            setEditRideData(null);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '1.5em' }}>
              {isBidRide ? t('myRides.dialog.bidDetails', 'Bid Ride Details') : isEditingRide ? t('myRides.dialog.editRide', 'Edit Ride Details') : t('myRides.dialog.rideDetails', 'Ride Details')}
            </Typography>
            <IconButton onClick={() => {
              if (isEditingRide) {
                handleCancelEditRide();
              } else {
                setDetailDialogOpen(false);
                setIsEditingRide(false);
                setEditRideData(null);
              }
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2}>
              {/* Status Alerts */}
              {!isEditingRide && hasBookings && !isBidRide && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '1.2em' } }}>
                    {t('myRides.dialog.lockedDueToBookings', 'This ride has confirmed bookings and cannot be edited or deleted.')}
                  </Alert>
                </Grid>
              )}
              {!isEditingRide && isAcceptedBid && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success" sx={{ '& .MuiAlert-message': { fontSize: '1.2em' } }}>
                    {t('myRides.dialog.bidFinalized', 'This bid has been accepted! The trip is now finalized and cannot be modified.')}
                  </Alert>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                {isBidRide && (
                  <Chip
                    label="Bid Ride"
                    size="small"
                    sx={{
                      mb: 1,
                      bgcolor: '#9c27b0',
                      color: 'white',
                      fontSize: '1.5em',
                      fontWeight: 'bold',
                      height: '2em'
                    }}
                  />
                )}
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1.5em' }}>
                  {selectedRide.originCity.name}, {selectedRide.originCity.province} → {selectedRide.destinationCity.name}, {selectedRide.destinationCity.province}
                </Typography>
                <Chip
                  label={getStatusText(selectedRide.status)}
                  color={getStatusColor(selectedRide.status) as any}
                  size="small"
                />
                {isBidRide && (selectedRide as any).bidStatus && (
                  <Chip
                    label={(selectedRide as any).bidStatus}
                    size="small"
                    color={(selectedRide as any).bidStatus === 'ACCEPTED' ? 'success' : (selectedRide as any).bidStatus === 'REJECTED' ? 'error' : 'warning'}
                    sx={{ ml: 1, fontSize: '1.5em', fontWeight: 'bold', height: '2em' }}
                  />
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em', mb: 1 }}>Departure Date & Time</Typography>
                {isEditingRide ? (
                  <DateTimePicker
                    label="Departure Date & Time"
                    value={editRideData.departureDateTime}
                    onChange={(newValue) => setEditRideData({ ...editRideData, departureDateTime: newValue || new Date() })}
                    slots={{
                      day: CustomPickersDay
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: { '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }
                      }
                    }}
                  />
                ) : (
                  <Typography variant="body1" sx={{ fontSize: '1.5em' }}>{date} at {time}</Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em', mb: 1 }}>Price per Seat</Typography>
                {isEditingRide ? (
                  <TextField
                    type="number"
                    value={editRideData.pricePerPerson}
                    onChange={(e) => setEditRideData({ ...editRideData, pricePerPerson: e.target.value })}
                    fullWidth
                    InputProps={{
                      startAdornment: <Typography sx={{ fontSize: '1.5em', mr: 1 }}>$</Typography>
                    }}
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                ) : (
                  <Typography variant="body1" color="primary.main" sx={{ fontWeight: 'bold', fontSize: '1.5em' }}>
                    ${selectedRide.pricePerPerson}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em', mb: 1 }}>Total Seats</Typography>
                {isEditingRide ? (
                  <TextField
                    type="number"
                    value={editRideData.totalSeats}
                    onChange={(e) => setEditRideData({ ...editRideData, totalSeats: e.target.value })}
                    fullWidth
                    inputProps={{ min: 1, max: 10 }}
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                ) : (
                  <Typography variant="body1" sx={{ fontSize: '1.5em' }}>
                    {selectedRide.availableSeats} / {selectedRide.totalSeats}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em', mb: 1 }}>Estimated Duration (minutes)</Typography>
                {isEditingRide ? (
                  <TextField
                    type="number"
                    value={editRideData.estimatedDuration || ''}
                    onChange={(e) => setEditRideData({ ...editRideData, estimatedDuration: e.target.value ? parseInt(e.target.value) : null })}
                    fullWidth
                    placeholder="Optional"
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                ) : selectedRide.estimatedDuration ? (
                  <Typography variant="body1" sx={{ fontSize: '1.5em' }}>
                    {Math.floor(selectedRide.estimatedDuration / 60)}h {selectedRide.estimatedDuration % 60}m
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.5em' }}>Not specified</Typography>
                )}
              </Grid>

              {selectedRide.vehicle && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em' }}>Vehicle</Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.5em' }}>
                    {selectedRide.vehicle.year} {selectedRide.vehicle.make?.name || selectedRide.vehicle.make} {selectedRide.vehicle.model?.name || selectedRide.vehicle.model} - {t(`vehicle.colorNames.${selectedRide.vehicle.color}`, selectedRide.vehicle.color)} ({selectedRide.vehicle.licensePlate})
                  </Typography>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em', mb: 1 }}>Pickup Details</Typography>
                {isEditingRide ? (
                  <TextField
                    value={editRideData.originDetails}
                    onChange={(e) => setEditRideData({ ...editRideData, originDetails: e.target.value })}
                    fullWidth
                    placeholder="Optional pickup location details"
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                ) : (
                  <TranslatingText text={selectedRide.originDetails || 'Not specified'} variant="body1" sx={{ fontSize: '1.5em' }} />
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em', mb: 1 }}>Drop-off Details</Typography>
                {isEditingRide ? (
                  <TextField
                    value={editRideData.destinationDetails}
                    onChange={(e) => setEditRideData({ ...editRideData, destinationDetails: e.target.value })}
                    fullWidth
                    placeholder="Optional drop-off location details"
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                ) : (
                  <TranslatingText text={selectedRide.destinationDetails || 'Not specified'} variant="body1" sx={{ fontSize: '1.5em' }} />
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em', mb: 1 }}>Preferences</Typography>
                {isEditingRide ? (
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editRideData.allowsSmoking}
                          onChange={(e) => setEditRideData({ ...editRideData, allowsSmoking: e.target.checked })}
                        />
                      }
                      label="Smoking Allowed"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '1.5em' } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editRideData.allowsPets}
                          onChange={(e) => setEditRideData({ ...editRideData, allowsPets: e.target.checked })}
                        />
                      }
                      label="Pets Allowed"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '1.5em' } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editRideData.allowsLargeLuggage}
                          onChange={(e) => setEditRideData({ ...editRideData, allowsLargeLuggage: e.target.checked })}
                        />
                      }
                      label="Large Luggage Allowed"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '1.5em' } }}
                    />
                  </FormGroup>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    <Chip label={selectedRide.allowsSmoking ? '🚬 Smoking Allowed' : '🚭 No Smoking'} size="small" />
                    <Chip label={selectedRide.allowsPets ? '🐕 Pets Allowed' : '🚫 No Pets'} size="small" />
                    <Chip label={selectedRide.allowsLargeLuggage ? '🧳 Large Luggage OK' : '👜 Small Bags Only'} size="small" />
                  </Box>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em', mb: 1 }}>Additional Notes</Typography>
                {isEditingRide ? (
                  <TextField
                    value={editRideData.additionalNotes}
                    onChange={(e) => setEditRideData({ ...editRideData, additionalNotes: e.target.value })}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Optional additional notes"
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                ) : (
                  <TranslatingText text={selectedRide.additionalNotes || 'No additional notes'} variant="body1" sx={{ fontSize: '1.5em' }} />
                )}
              </Grid>

              {isBidRide && (selectedRide as any).message && (
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: '1.5em' }}>
                    Your Message
                  </Typography>
                  <TranslatingText
                    text={(selectedRide as any).message}
                    variant="body1"
                    sx={{ fontSize: '1.5em', p: 2, bgcolor: '#f3e5f5', borderRadius: 1 }}
                  />
                </Grid>
              )}
              {isBidRide && (selectedRide as any).passenger && (
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: '1.5em' }}>
                    Passenger
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={(selectedRide as any).passenger.profilePicture}
                      sx={{ cursor: 'pointer' }}
                      onClick={(e) => handleProfileClick(e, (selectedRide as any).passenger.id)}
                    >
                      {((selectedRide as any).passenger.preferredName || (selectedRide as any).passenger.firstName)[0]}{(selectedRide as any).passenger.lastName[0]}
                    </Avatar>
                    <Typography variant="body1" sx={{ fontSize: '1.5em' }}>
                      {(selectedRide as any).passenger.preferredName || (selectedRide as any).passenger.firstName} {(selectedRide as any).passenger.lastName ? (selectedRide as any).passenger.lastName.charAt(0) + '.' : ''}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Manual Approval & Booking details for driver */}
              {user && user.id === selectedRide.driverId ? (
                <Grid size={{ xs: 12 }}>
                  {/* Pending Join Requests */}
                  {selectedRide.bookings && selectedRide.bookings.filter((b: any) => b.status === 'PENDING').length > 0 && (
                    <Box sx={{ mt: 2, mb: 3 }}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'warning.main', fontSize: '1.2em' }}>
                        Pending Join Requests
                      </Typography>
                      {selectedRide.bookings.filter((b: any) => b.status === 'PENDING').map((booking: any) => (
                        <Paper key={booking.id} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.03)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                              <Avatar
                                src={booking.passenger.profilePicture}
                                sx={{ width: 48, height: 48, cursor: 'pointer' }}
                                onClick={(e) => handleProfileClick(e, booking.passenger.id)}
                              >
                                {(booking.passenger.preferredName || booking.passenger.firstName)?.[0]}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                                  {booking.passenger.preferredName || booking.passenger.firstName} {booking.passenger.lastName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Rating: ⭐ {booking.passenger.passengerRating?.toFixed(1) || 'New'} ({booking.passenger.totalTripsAsPassenger || 0} rides)
                                </Typography>
                                {booking.passenger.bio && (
                                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', fontSize: '0.95em' }}>
                                    "{booking.passenger.bio}"
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {booking.seatsBooked} {booking.seatsBooked === 1 ? 'seat' : 'seats'} requested
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                Payment: {booking.paymentMethod === 'CREDIT_CARD' ? '💳 Stripe' : '💵 Cash'} ({booking.paymentStatus})
                              </Typography>
                            </Box>
                          </Box>
                          {booking.specialRequests && (
                            <Box sx={{ mt: 1.5, p: 1, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>Message:</Typography>
                              <Typography variant="body2">{booking.specialRequests}</Typography>
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              disabled={actionLoading === booking.id}
                              onClick={() => handleRequestAction(booking.id, 'reject')}
                            >
                              Reject
                            </Button>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              disabled={actionLoading === booking.id}
                              onClick={() => handleRequestAction(booking.id, 'approve')}
                            >
                              Approve
                            </Button>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}

                  {/* Confirmed Passengers */}
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, fontSize: '1.2em' }}>
                      Confirmed Passengers ({selectedRide.bookings?.filter((b: any) => b.status === 'CONFIRMED').reduce((acc: number, b: any) => acc + b.seatsBooked, 0) || 0} seats booked)
                    </Typography>
                    {selectedRide.bookings && selectedRide.bookings.filter((b: any) => b.status === 'CONFIRMED').length > 0 ? (
                      selectedRide.bookings.filter((b: any) => b.status === 'CONFIRMED').map((booking: any) => (
                        <Paper key={booking.id} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                              <Avatar
                                src={booking.passenger.profilePicture}
                                sx={{ width: 40, height: 40, cursor: 'pointer' }}
                                onClick={(e) => handleProfileClick(e, booking.passenger.id)}
                              >
                                {(booking.passenger.preferredName || booking.passenger.firstName)?.[0]}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  {booking.passenger.preferredName || booking.passenger.firstName} {booking.passenger.lastName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Rating: ⭐ {booking.passenger.passengerRating?.toFixed(1) || 'New'} • Phone: {booking.passenger.phoneNumber || 'Hidden'}
                                </Typography>
                                {booking.passenger.bio && (
                                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', fontSize: '0.9em' }}>
                                    "{booking.passenger.bio}"
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {booking.seatsBooked} {booking.seatsBooked === 1 ? 'seat' : 'seats'}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                Payment: {booking.paymentMethod === 'CREDIT_CARD' ? '💳 Stripe' : '💵 Cash'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No confirmed passengers yet.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ) : (
                /* Original Passenger View */
                selectedRide.passengers && selectedRide.passengers.length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: '1.5em' }}>
                      Passengers ({selectedRide.bookedSeats || selectedRide.passengers.length} seats booked)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedRide.passengers.map((passenger) => (
                        <Chip
                          key={passenger.id}
                          label={`${passenger.preferredName || passenger.firstName || ''} ${passenger.lastName ? passenger.lastName.charAt(0) + '.' : ''}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Grid>
                )
              )}

              {/* Ask Question Button - Show if allowChat is true and user is not the driver */}
              {!isEditingRide && (selectedRide.allowChat === undefined || selectedRide.allowChat === true) && user && user.id !== selectedRide.driverId && (
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <MessageButton
                      userId={selectedRide.driverId}
                      contextType={cleanContextType as any}
                      contextId={cleanContextId}
                      label="Ask Question About This Ride"
                      variant="outlined"
                      color="primary"
                      size="large"
                      sx={{
                        py: 1.5,
                        fontSize: '1.5em',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          {isEditingRide ? (
            <>
              <Button
                onClick={handleCancelEditRide}
                sx={{ fontSize: '1.5em' }}
                disabled={savingRide}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveRide}
                variant="contained"
                sx={{ fontSize: '1.5em' }}
                disabled={savingRide}
              >
                {savingRide ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              {/* Only show Edit for: non-bid rides that are active, OR PENDING bids */}
              {(selectedRide.status === 'ACTIVE' || selectedRide.status === 'PENDING_VERIFICATION') &&
                (!selectedRide.passengers || selectedRide.passengers.length === 0) &&
                (!selectedRide.bookedSeats || selectedRide.bookedSeats === 0) &&
                !(isBidRide && (selectedRide as any).bidStatus === 'ACCEPTED') && (
                  <Button
                    onClick={handleStartEditRide}
                    variant="contained"
                    startIcon={<EditIcon />}
                    sx={{ fontSize: '1.5em' }}
                  >
                    Edit
                  </Button>
                )}
              <Button
                onClick={() => {
                  setDetailDialogOpen(false);
                  setIsEditingRide(false);
                  setEditRideData(null);
                }}
                sx={{ fontSize: '1.5em' }}
              >
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const TIME_FLEXIBILITY_OPTIONS = [
    { value: 0, label: 'Exact' },
    { value: 1, label: '±1h' },
    { value: 2, label: '±2h' },
    { value: 4, label: '±4h' },
  ];

  const renderRequestDialog = () => {
    if (!selectedRequest) return null;

    const { date, time } = formatDateTime(selectedRequest.preferredDateTime);
    const hasBids = selectedRequest.totalBids > 0 || selectedRequest.acceptedBid;
    const isOwner =
      selectedRequest.passengerId === user?.id ||
      selectedRequest.passenger?.id === user?.id ||
      selectedRequest.userId === user?.id;
    // Status can be 'OPEN' (from backend) or 'ACTIVE' (remapped in loadAllUnifiedRides)
    const isOpenStatus = selectedRequest.status === 'OPEN' || selectedRequest.status === 'ACTIVE';
    const canEdit = !hasBids && isOpenStatus && isOwner;
    const canDelete = isOpenStatus && isOwner;

    return (
      <Dialog
        open={requestDialogOpen}
        onClose={() => {
          setRequestDialogOpen(false);
          setIsEditingRequest(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '1.5em' }}>
              {isEditingRequest ? t('myRides.dialog.editRequest', 'Edit Ride Request') : t('myRides.dialog.requestDetails', 'Ride Request Details')}
            </Typography>
            <IconButton onClick={() => {
              setRequestDialogOpen(false);
              setIsEditingRequest(false);
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Status Alerts */}
          {!isEditingRequest && hasBids && (
            <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '1.2em' } }}>
              {t('myRides.dialog.requestHasBids', 'This request has active bids. You cannot edit it while bids are pending, but you can still delete it.')}
            </Alert>
          )}
          {!isEditingRequest && !isOpenStatus && (
            <Alert severity="warning" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '1.2em' } }}>
              {t('myRides.dialog.requestInactive', 'This request is no longer active and cannot be edited or deleted.')}
            </Alert>
          )}

          {isEditingRequest && requestFormData ? (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {/* Origin City */}
                <Grid size={{ xs: 12, md: 5 }}>
                  <LocationAutocomplete
                    label={t('myRides.form.from', 'From')}
                    placeholder={t('myRides.form.fromPlaceholder', 'Enter origin city')}
                    icon={<LocationIcon color="action" />}
                    value={requestFormData.originCity}
                    onChange={(newValue) =>
                      setRequestFormData({ ...requestFormData, originCity: newValue })
                    }
                  />
                </Grid>

                {/* Swap Button */}
                <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <IconButton
                    onClick={() => {
                      setRequestFormData({
                        ...requestFormData,
                        originCity: requestFormData.destinationCity,
                        destinationCity: requestFormData.originCity,
                        originDetails: requestFormData.destinationDetails,
                        destinationDetails: requestFormData.originDetails,
                      });
                    }}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    <SwapIcon />
                  </IconButton>
                </Grid>

                {/* Destination City */}
                <Grid size={{ xs: 12, md: 5 }}>
                  <LocationAutocomplete
                    label={t('myRides.form.to', 'To')}
                    placeholder={t('myRides.form.toPlaceholder', 'Enter destination city')}
                    icon={<LocationIcon color="action" />}
                    value={requestFormData.destinationCity}
                    onChange={(newValue) =>
                      setRequestFormData({ ...requestFormData, destinationCity: newValue })
                    }
                  />
                </Grid>

                {/* Date/Time */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <DateTimePicker
                    label={t('myRides.form.preferredDateTime', 'Preferred Date & Time')}
                    value={requestFormData.preferredDateTime}
                    onChange={(newValue) =>
                      setRequestFormData({ ...requestFormData, preferredDateTime: newValue || new Date() })
                    }
                    slots={{
                      day: CustomPickersDay
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: { '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }
                      }
                    }}
                  />
                </Grid>

                {/* Time Flexibility */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <FormLabel sx={{ fontSize: '1.5em' }}>Time Flexibility</FormLabel>
                    <ButtonGroup variant="outlined" fullWidth>
                      {TIME_FLEXIBILITY_OPTIONS.map((option) => (
                        <Button
                          key={option.value}
                          variant={requestFormData.timeFlexibility === option.value ? 'contained' : 'outlined'}
                          onClick={() => setRequestFormData({ ...requestFormData, timeFlexibility: option.value })}
                          sx={{ flex: 1, fontSize: '1.5em' }}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </FormControl>
                </Grid>

                {/* Passenger Count */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Number of Passengers"
                    value={requestFormData.passengerCount}
                    onChange={(e) =>
                      setRequestFormData({ ...requestFormData, passengerCount: parseInt(e.target.value) || 1 })
                    }
                    inputProps={{ min: 1, max: 8 }}
                    required
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                </Grid>

                {/* Budget */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min Budget ($)"
                    value={requestFormData.minBudget}
                    onChange={(e) => setRequestFormData({ ...requestFormData, minBudget: e.target.value })}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1, fontSize: '1.5em' }}>$</Typography> }}
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Budget ($)"
                    value={requestFormData.maxBudget}
                    onChange={(e) => setRequestFormData({ ...requestFormData, maxBudget: e.target.value })}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1, fontSize: '1.5em' }}>$</Typography> }}
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                </Grid>

                {/* Special Requirements */}
                <Grid size={{ xs: 12 }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={requestFormData.needsLargeLuggage}
                          onChange={(e) =>
                            setRequestFormData({ ...requestFormData, needsLargeLuggage: e.target.checked })
                          }
                        />
                      }
                      label="Large Luggage"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '1.5em' } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={requestFormData.needsChildSeat}
                          onChange={(e) =>
                            setRequestFormData({ ...requestFormData, needsChildSeat: e.target.checked })
                          }
                        />
                      }
                      label="Child Seat"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '1.5em' } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={requestFormData.needsWheelchairAccess}
                          onChange={(e) =>
                            setRequestFormData({ ...requestFormData, needsWheelchairAccess: e.target.checked })
                          }
                        />
                      }
                      label="Wheelchair Access"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '1.5em' } }}
                    />
                  </FormGroup>
                </Grid>

                {/* Details */}
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Pickup Details"
                    value={requestFormData.originDetails}
                    onChange={(e) => setRequestFormData({ ...requestFormData, originDetails: e.target.value })}
                    multiline
                    rows={2}
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Drop-off Details"
                    value={requestFormData.destinationDetails}
                    onChange={(e) => setRequestFormData({ ...requestFormData, destinationDetails: e.target.value })}
                    multiline
                    rows={2}
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Special Requirements"
                    value={requestFormData.specialRequirements}
                    onChange={(e) => setRequestFormData({ ...requestFormData, specialRequirements: e.target.value })}
                    multiline
                    rows={3}
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={requestFormData.description}
                    onChange={(e) => setRequestFormData({ ...requestFormData, description: e.target.value })}
                    multiline
                    rows={3}
                    sx={{ '& .MuiInputBase-input': { fontSize: '1.5em' }, '& .MuiInputLabel-root': { fontSize: '1.5em' } }}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 1, fontSize: '1.5em' }}>
                  {selectedRequest.originCity.name} → {selectedRequest.destinationCity.name}
                </Typography>
                <Chip label={selectedRequest.status} size="small" color={selectedRequest.status === 'OPEN' ? 'success' : 'default'} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em' }}>Preferred Date & Time</Typography>
                <Typography variant="body1" sx={{ fontSize: '1.5em' }}>{date} at {time}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em' }}>Passengers</Typography>
                <Typography variant="body1" sx={{ fontSize: '1.5em' }}>{selectedRequest.passengerCount}</Typography>
              </Grid>
              {selectedRequest.minBudget && selectedRequest.maxBudget && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em' }}>Budget</Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.5em' }}>${selectedRequest.minBudget} - ${selectedRequest.maxBudget}</Typography>
                </Grid>
              )}
              {selectedRequest.originDetails && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em' }}>Pickup Details</Typography>
                  <TranslatingText text={selectedRequest.originDetails} variant="body1" sx={{ fontSize: '1.5em' }} />
                </Grid>
              )}
              {selectedRequest.destinationDetails && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em' }}>Drop-off Details</Typography>
                  <TranslatingText text={selectedRequest.destinationDetails} variant="body1" sx={{ fontSize: '1.5em' }} />
                </Grid>
              )}
              {hasBids && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.5em' }}>Bids</Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.5em' }}>{selectedRequest.totalBids} bid(s)</Typography>
                </Grid>
              )}

              {/* Ask Question Button - Show if allowChat is true and user is not the passenger */}
              {!isEditingRequest && (selectedRequest.allowChat === undefined || selectedRequest.allowChat === true) && selectedRequest.passengerId && user && user.id !== selectedRequest.passengerId && (
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <MessageButton
                      userId={selectedRequest.passengerId}
                      contextType="request"
                      contextId={selectedRequest.id}
                      label="Ask Question About This Ride Request"
                      variant="outlined"
                      color="secondary"
                      size="large"
                      sx={{
                        py: 1.5,
                        fontSize: '1.5em',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          {isEditingRequest ? (
            <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setIsEditingRequest(false)} sx={{ fontSize: '1.5em' }}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveRequest} sx={{ fontSize: '1.5em' }}>Save Changes</Button>
            </Box>
          ) : (
            <>
              {/* Left side: Delete button */}
              <Box>
                {canDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteRequest}
                    sx={{ fontSize: '1.5em' }}
                  >
                    Delete Request
                  </Button>
                )}
              </Box>
              {/* Right side: Close + View Bids + Edit buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => {
                  setRequestDialogOpen(false);
                  setIsEditingRequest(false);
                }} sx={{ fontSize: '1.5em' }}>Close</Button>
                {hasBids && isOwner && (
                  <Button
                    variant="outlined"
                    color="info"
                    onClick={async () => {
                      setRequestDialogOpen(false);
                      setBidsDialogOpen(true);
                      await loadRequestBids(selectedRequest.id);
                    }}
                    sx={{ fontSize: '1.5em' }}
                  >
                    View Bids ({selectedRequest.totalBids || 0})
                  </Button>
                )}
                {canEdit && (
                  <Button variant="contained" startIcon={<EditIcon />} onClick={handleEditRequest} sx={{ fontSize: '1.5em' }}>
                    Edit Request
                  </Button>
                )}
              </Box>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderCalendarDayDialog = () => {
    if (!selectedCalendarDate) return null;

    if (isDriver) {
      const dayRides = filteredRides.filter(ride => {
        const rideDate = new Date(ride.departureDateTime);
        return rideDate.toDateString() === selectedCalendarDate.toDateString();
      });

      return (
        <Dialog
          open={calendarDialogOpen}
          onClose={() => setCalendarDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: '1.5em' }}>
                {t('myRides.dialog.ridesOn', 'Rides on {{date}}', { date: selectedCalendarDate.toLocaleDateString(i18n.language) })}
              </Typography>
              <IconButton onClick={() => setCalendarDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {dayRides.map(ride => {
              const { time } = formatDateTime(ride.departureDateTime);
              return (
                <Card
                  key={ride.id}
                  sx={{
                    mb: 2,
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 3 },
                  }}
                  onClick={() => {
                    setSelectedRide(ride);
                    setCalendarDialogOpen(false);
                    setDetailDialogOpen(true);
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {ride.originCity.name} → {ride.destinationCity.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Departure: {time}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={getStatusText(ride.status)}
                            color={getStatusColor(ride.status) as any}
                            size="small"
                          />
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, ride);
                        }}
                      >
                        <MoreIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCalendarDialogOpen(false)} sx={{ fontSize: '1.5em' }}>Close</Button>
          </DialogActions>
        </Dialog>
      );
    } else {
      // Passenger view: show bookings and requests
      const dayBookings = bookings.filter(booking => {
        const rideDate = new Date(booking.ride.departureDateTime);
        return rideDate.toDateString() === selectedCalendarDate.toDateString();
      });

      const dayRequests = rideRequests.filter(request => {
        const requestDate = new Date(request.preferredDateTime);
        return requestDate.toDateString() === selectedCalendarDate.toDateString();
      });

      return (
        <Dialog
          open={calendarDialogOpen}
          onClose={() => setCalendarDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: '1.5em' }}>
                {t('myRides.dialog.itemsOn', 'Items on {{date}}', { date: selectedCalendarDate.toLocaleDateString(i18n.language) })}
              </Typography>
              <IconButton onClick={() => setCalendarDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {dayRequests.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary', fontSize: '1.5em' }}>
                  Ride Requests
                </Typography>
                {dayRequests.map(request => (
                  <Card
                    key={request.id}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 3 },
                    }}
                    onClick={() => {
                      handleRequestClick(request);
                      setCalendarDialogOpen(false);
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.5em' }}>
                        {request.originCity.name} → {request.destinationCity.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.5em' }}>
                        Preferred: {formatDateTime(request.preferredDateTime).time}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
            {dayBookings.length > 0 && (
              <Box>
                {dayRequests.length > 0 && (
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary', fontSize: '1.5em' }}>
                    Bookings
                  </Typography>
                )}
                {dayBookings.map(booking => {
                  const ride = booking.ride;
                  const { time } = formatDateTime(ride.departureDateTime);
                  return (
                    <Card
                      key={booking.id}
                      sx={{
                        mb: 1,
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 3 },
                      }}
                      onClick={() => navigate(`/ride/${ride.id}`)}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {ride.originCity.name} → {ride.destinationCity.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Departure: {time}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={booking.status}
                            size="small"
                            color={booking.status === 'CONFIRMED' ? 'success' : 'default'}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
            {dayBookings.length === 0 && dayRequests.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, fontSize: '1.5em' }}>
                No items on this date
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCalendarDialogOpen(false)} sx={{ fontSize: '1.5em' }}>Close</Button>
          </DialogActions>
        </Dialog>
      );
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', fontSize: '1.5em' }}>
            {isDriver ? t('myRides.title.driver', '🚗 My Rides') : t('myRides.title.passenger', '🎒 My Rides & Requests')}
          </Typography>
          {isDriver && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/create-ride')}
              sx={{ fontSize: '1.5em' }}
            >
              {t('myRides.actions.createRide', 'Create New Ride')}
            </Button>
          )}
        </Box>

        {/* Success/Error Messages */}
        {message && (
          <Alert
            severity={messageType}
            sx={{ mb: 3, '& .MuiAlert-message': { fontSize: '1.5em' } }}
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        )}

        {/* Tabs and Filters */}
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': { fontSize: '1.4rem', fontWeight: 'bold', minHeight: '64px' },
                  '& .MuiTabs-indicator': { height: '4px' }
                }}
              >
                <Tab label={t('myRides.tabs.upcoming', 'Upcoming')} />
                <Tab label={t('myRides.tabs.completed', 'Completed')} />
                <Tab label={t('myRides.tabs.cancelled', 'Cancelled')} />
                <Tab label={t('myRides.tabs.saved', 'Saved')} />
              </Tabs>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ToggleButtonGroup
                  value={viewMode === 'calendar' ? 'calendar' : (activeFilter === 'calendar' ? 'all' : activeFilter)}
                  exclusive
                  onChange={(e, newValue) => {
                    if (!newValue) return;
                    if (newValue === 'calendar') {
                      setViewMode('calendar');
                      setActiveFilter('calendar');
                    } else if (newValue === 'all') {
                      setViewMode('list');
                      setActiveFilter('calendar');
                    } else {
                      setViewMode('list');
                      setActiveFilter(newValue as any);
                    }
                  }}
                  sx={{
                    '& .MuiToggleButton-root': {
                      fontSize: '1.5rem',
                      px: 3,
                      py: 1.5,
                      fontWeight: '800',
                      borderRadius: '12px !important',
                      mx: 0.5,
                      border: '1px solid rgba(0,0,0,0.1) !important',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="calendar">
                    <CalendarIcon sx={{ mr: 1, fontSize: '1.8rem' }} /> {t('common.calendar', 'Calendar')}
                  </ToggleButton>
                  <ToggleButton value="weekly">
                    {t('common.weekly', 'Weekly')}
                  </ToggleButton>
                  <ToggleButton value="monthly">
                    {t('common.monthly', 'Monthly')}
                  </ToggleButton>
                  <ToggleButton value="all">
                    <ListIcon sx={{ mr: 1, fontSize: '1.8rem' }} /> {t('common.all', 'All')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
              {activeFilter !== 'calendar' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, background: 'rgba(0,0,0,0.03)', p: 1, px: 3, borderRadius: '50px' }}>
                  <IconButton
                    onClick={activeFilter === 'weekly' ? handlePreviousWeek : handlePreviousMonth}
                    sx={{ bgcolor: 'white', border: '1px solid rgba(0,0,0,0.1)', '&:hover': { bgcolor: '#f5f5f5' } }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: '2rem' }} />
                  </IconButton>
                  <Typography sx={{ fontWeight: '900', minWidth: 200, textAlign: 'center', fontSize: '1.5rem', color: 'primary.main' }}>
                    {activeFilter === 'weekly' ? (
                      t('myRides.weekOf', 'Week of {{date}}', { date: currentWeek.toLocaleDateString(i18n.language) })
                    ) : (
                      `${new Date(calendarYear, calendarMonth).toLocaleString(i18n.language, { month: 'long' })} ${calendarYear}`
                    )}
                  </Typography>
                  <IconButton
                    onClick={activeFilter === 'weekly' ? handleNextWeek : handleNextMonth}
                    sx={{ bgcolor: 'white', border: '1px solid rgba(0,0,0,0.1)', '&:hover': { bgcolor: '#f5f5f5' } }}
                  >
                    <ChevronRightIcon sx={{ fontSize: '2rem' }} />
                  </IconButton>
                  <Button
                    variant="outlined"
                    onClick={handleToday}
                    sx={{
                      ml: 2,
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      borderRadius: '20px',
                      px: 3
                    }}
                  >
                    {t('common.today', 'Today')}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary', fontWeight: 'bold' }}>
                    {t('myRides.showingAll', 'Showing all rides for the selected status')}
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 3,
                    p: 1.5,
                    px: 3,
                    bgcolor: 'rgba(33, 150, 243, 0.05)',
                    borderRadius: '50px',
                    border: '1px solid rgba(33, 150, 243, 0.1)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2196F3', border: '1px solid #fff' }} />
                      <Typography variant="body2" sx={{ fontWeight: '800', fontSize: '1rem', color: '#1e293b' }}>
                        {t('myRides.legend.driverRide', 'My Rides')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4CAF50', border: '1px solid #fff' }} />
                      <Typography variant="body2" sx={{ fontWeight: '800', fontSize: '1rem', color: '#1e293b' }}>
                        {t('myRides.legend.booking', 'My Bookings')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#9C27B0', border: '1px solid #fff' }} />
                      <Typography variant="body2" sx={{ fontWeight: '800', fontSize: '1rem', color: '#1e293b' }}>
                        {t('myRides.legend.request', 'Ride Requests')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FF9800', border: '1px solid #fff' }} />
                      <Typography variant="body2" sx={{ fontWeight: '800', fontSize: '1rem', color: '#1e293b' }}>
                        {t('myRides.legend.bid', 'My Bids')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#10b981', border: '1px solid #fff' }} />
                      <Typography variant="body2" sx={{ fontWeight: '800', fontSize: '1rem', color: '#1e293b' }}>
                        {t('myRides.legend.saved', 'Saved Rides')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Nature Filters (Offered, Bid, Joined, Requested) */}
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <ToggleButtonGroup
                value={natureFilter}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setNatureFilter(newValue);
                  }
                }}
                sx={{
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  '& .MuiToggleButton-root': {
                    fontSize: '1.2rem',
                    px: 3,
                    py: 1,
                    fontWeight: '800',
                    borderRadius: '20px !important',
                    mx: 1,
                    my: 0.5,
                    border: '1px solid rgba(0,0,0,0.1) !important',
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      }
                    },
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.05)',
                    }
                  }
                }}
              >
                <ToggleButton value="all">
                  ✨ {t('myRides.filters.all', 'All')}
                </ToggleButton>
                <ToggleButton value="offered">
                  🚗 {t('myRides.filters.offered', 'Offered')}
                </ToggleButton>
                <ToggleButton value="bid">
                  🎯 {t('myRides.filters.bid', 'Bids')}
                </ToggleButton>
                <ToggleButton value="joined">
                  🎒 {t('myRides.filters.joined', 'Joined')}
                </ToggleButton>
                <ToggleButton value="requested">
                  📋 {t('myRides.filters.requested', 'Requested')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Paper>

        {/* Rides List or Calendar */}
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(1px)',
              pointerEvents: 'none'
            }}>
              <CircularProgress size={60} thickness={4} />
            </Box>
          )}

          {isDriver ? (
            filteredRides.length > 0 ? (
              <Box>
                {viewMode === 'list' ? (
                  <Grid container spacing={3}>
                    {filteredRides.map(renderRideCard)}
                  </Grid>
                ) : (
                  renderCalendarView()
                )}
              </Box>
            ) : null
          ) : (bookings.length > 0 || rideRequests.length > 0) ? (
            <Box>
              {viewMode === 'list' ? (
                <>
                  {/* Partition filteredRides by type */}
                  {(() => {
                    const items = filteredRides;
                    const reqs = items.filter(r => r.rideType === 'request');
                    const bks = items.filter(r => r.rideType === 'booking').map(r => r.booking);
                    const svds = items.filter(r => r.rideType === 'saved');

                    return (
                      <>
                        {/* Show Ride Requests */}
                        {reqs.length > 0 && (
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: '900', color: 'text.secondary', fontSize: '1.8rem', borderLeft: '6px solid #2196f3', pl: 2 }}>
                              {t('myRides.headers.rideRequests', 'My Ride Requests')}
                            </Typography>
                            <Grid container spacing={4}>
                              {reqs.map(renderRideRequestCard)}
                            </Grid>
                          </Box>
                        )}

                        {/* Show Bookings */}
                        {bks.length > 0 && (
                          <Box>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: '900', color: 'text.secondary', fontSize: '1.8rem', borderLeft: '6px solid #4CAF50', pl: 2 }}>
                              {t('myRides.headers.bookings', 'My Bookings')}
                            </Typography>
                            <Grid container spacing={4}>
                              {bks.map(renderBookingCard)}
                            </Grid>
                          </Box>
                        )}

                        {/* Show Saved Rides */}
                        {svds.length > 0 && (
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: '900', color: 'text.secondary', fontSize: '1.8rem', borderLeft: '6px solid #10b981', pl: 2 }}>
                              {t('myRides.headers.savedRides', 'Saved Rides')}
                            </Typography>
                            <Grid container spacing={4}>
                              {svds.map(renderRideCard)}
                            </Grid>
                          </Box>
                        )}

                        {reqs.length === 0 && bks.length === 0 && svds.length === 0 && (
                          <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography variant="h5" color="text.secondary">
                              {t('myRides.messages.noFilteredItems', 'No items match the selected time filter.')}
                            </Typography>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                renderCalendarView()
              )}
            </Box>
          ) : null}
        </Box>

        {/* Empty State */}
        {
          !loading && (isDriver ? filteredRides.length === 0 : bookings.length === 0 && rideRequests.length === 0) && (
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: 3,
              }}
            >
              <CarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontSize: '1.5em' }}>
                {t('myRides.messages.noItemsFound', 'No {{tab}} {{type}} found', {
                  tab: activeTab === 0 ? t('myRides.tabs.upcoming') : activeTab === 1 ? t('myRides.tabs.completed') : activeTab === 2 ? t('myRides.tabs.cancelled') : t('myRides.tabs.saved'),
                  type: isDriver ? t('common.rides') : t('common.bookingsRequests')
                })}
              </Typography>
              {isDriver && activeTab === 0 && (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '1.5em' }}>
                    {t('myRides.messages.startEarning', 'Start earning by creating your first ride listing.')}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/create-ride')}
                    sx={{ fontSize: '1.5em' }}
                  >
                    {t('myRides.actions.createFirstRide', 'Create Your First Ride')}
                  </Button>
                </>
              )}
              {!isDriver && activeTab === 0 && (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '1.5em' }}>
                    {t('myRides.messages.noBookings', "You haven't booked any rides yet.")}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/find-rides')}
                    sx={{ fontSize: '1.5em' }}
                  >
                    {t('myRides.actions.findRides', 'Find Rides')}
                  </Button>
                </>
              )}
            </Paper>
          )
        }

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleViewDetails} sx={{ fontSize: '1.5em' }}>
            <ViewIcon fontSize="small" sx={{ mr: 1 }} />
            {t('common.viewDetails', 'View Details')}
          </MenuItem>
          <MenuItem onClick={handleDuplicateRide} sx={{ fontSize: '1.5em' }}>
            <DuplicateIcon fontSize="small" sx={{ mr: 1 }} />
            {t('common.duplicate', 'Duplicate')}
          </MenuItem>
          {(selectedRide?.status === 'ACTIVE' || selectedRide?.status === 'PENDING_VERIFICATION') &&
            (!selectedRide.passengers || selectedRide.passengers.length === 0) &&
            (!selectedRide.bookedSeats || selectedRide.bookedSeats === 0) && (
              <MenuItem onClick={handleEditRide} sx={{ fontSize: '1.5em' }}>
                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                {t('common.editRide', 'Edit Ride')}
              </MenuItem>
            )}
          {selectedRide?.status === 'ACTIVE' && (
            <MenuItem onClick={handleCancelRide} sx={{ color: 'error.main', fontSize: '1.5em' }}>
              <CloseIcon fontSize="small" sx={{ mr: 1 }} />
              {t('common.cancelRide', 'Cancel Ride')}
            </MenuItem>
          )}
        </Menu>

        {/* Detail Dialog */}
        {renderDetailDialog()}

        {/* Saved Ride Details Modal (Modern Gen Alpha Design) */}
        {selectedSavedRide && (
          <RideDetailsModal
            isOpen={savedRideDialogOpen}
            onClose={() => {
              setSavedRideDialogOpen(false);
              setSelectedSavedRide(null);
            }}
            ride={selectedSavedRide}
            onJoin={(rideId: string, seats: number) => {
              setSavedRideDialogOpen(false);
              setSelectedSavedRide(null);
              navigate(`/ride/${rideId}`, {
                state: {
                  ride: selectedSavedRide,
                  showBooking: true,
                  initialSeats: seats
                }
              });
            }}
          />
        )}

        {/* Calendar Day Dialog */}
        {renderCalendarDayDialog()}

        {/* Request Detail/Edit Dialog */}
        {renderRequestDialog()}

        {/* Bids Dialog */}
        <Dialog
          open={bidsDialogOpen}
          onClose={() => {
            setBidsDialogOpen(false);
            setSelectedRequestBids([]);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: '1.5em' }}>
                {t('myRides.dialog.bidsTitle', 'Bids for Your Ride Request')}
              </Typography>
              <IconButton onClick={() => {
                setBidsDialogOpen(false);
                setSelectedRequestBids([]);
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedRequest && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.5em', mb: 1 }}>
                  {selectedRequest.originCity.name} → {selectedRequest.destinationCity.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.5em' }}>
                  Preferred: {formatDateTime(selectedRequest.preferredDateTime).date} at {formatDateTime(selectedRequest.preferredDateTime).time}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 2 }} />

            {/* Sorting Controls */}
            {selectedRequestBids.length > 0 && !loadingBids && (
              <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontSize: '1.5em', fontWeight: 'bold' }}>
                  Sort by:
                </Typography>
                <Button
                  variant={priceSortOrder !== null ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    if (priceSortOrder === 'asc') {
                      setPriceSortOrder('desc');
                    } else if (priceSortOrder === 'desc') {
                      setPriceSortOrder(null);
                    } else {
                      setPriceSortOrder('asc');
                    }
                  }}
                  sx={{ fontSize: '1.5em' }}
                >
                  Price {priceSortOrder === 'asc' ? '$ → $$$' : priceSortOrder === 'desc' ? '$$$ → $' : ''}
                </Button>
                <Button
                  variant={ratingSortOrder !== null ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    if (ratingSortOrder === 'asc') {
                      setRatingSortOrder('desc');
                    } else if (ratingSortOrder === 'desc') {
                      setRatingSortOrder(null);
                    } else {
                      setRatingSortOrder('asc');
                    }
                  }}
                  sx={{ fontSize: '1.5em' }}
                >
                  Rating {ratingSortOrder === 'asc' ? '⭐ → ⭐⭐⭐⭐⭐' : ratingSortOrder === 'desc' ? '⭐⭐⭐⭐⭐ → ⭐' : ''}
                </Button>
              </Box>
            )}

            {loadingBids ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : selectedRequestBids.length > 0 ? (
              <Box>
                {[...selectedRequestBids]
                  .sort((a, b) => {
                    // Sort by BOTH price AND rating (both are always applied)
                    const priceA = a.priceOffer;
                    const priceB = b.priceOffer;
                    const ratingA = a.driver?.driverRating || 0;
                    const ratingB = b.driver?.driverRating || 0;

                    // Price comparison (if price sorting is active)
                    let priceComparison = 0;
                    if (priceSortOrder === 'asc') {
                      priceComparison = priceA - priceB;
                    } else if (priceSortOrder === 'desc') {
                      priceComparison = priceB - priceA;
                    }

                    // Rating comparison (if rating sorting is active)
                    let ratingComparison = 0;
                    if (ratingSortOrder === 'asc') {
                      ratingComparison = ratingA - ratingB;
                    } else if (ratingSortOrder === 'desc') {
                      ratingComparison = ratingB - ratingA;
                    }

                    // Apply both sorts: price first (if active), then rating (if active)
                    if (priceSortOrder !== null && priceComparison !== 0) {
                      return priceComparison;
                    }
                    if (ratingSortOrder !== null && ratingComparison !== 0) {
                      return ratingComparison;
                    }

                    // If both are equal or inactive, maintain original order
                    return 0;
                  })
                  .map((bid) => (
                    <Card
                      key={bid.id}
                      sx={{
                        mb: 2,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        },
                      }}
                      onClick={(e) => handleProfileClick(e, bid.driver.id)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                              bid.driver?.isLicenseVerified ? (
                                <VerifiedIcon
                                  sx={{
                                    fontSize: 18,
                                    color: '#4caf50',
                                    bgcolor: 'white',
                                    borderRadius: '50%',
                                    p: 0.25,
                                  }}
                                />
                              ) : null
                            }
                          >
                            <Avatar
                              src={bid.driver?.profilePicture}
                              sx={{ width: 60, height: 60 }}
                            >
                              {(bid.driver?.preferredName || bid.driver?.firstName)?.[0]}{bid.driver?.lastName?.[0]}
                            </Avatar>
                          </Badge>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.5em', mb: 0.5 }}>
                                {bid.driver?.preferredName || bid.driver?.firstName} {bid.driver?.lastName ? bid.driver?.lastName.charAt(0) + '.' : ''}
                              </Typography>
                              {bid.driver?.isLicenseVerified && (
                                <VerifiedIcon
                                  sx={{
                                    fontSize: 18,
                                    color: '#4caf50',
                                    mb: 0.5
                                  }}
                                  titleAccess="Verified Driver License"
                                />
                              )}
                            </Box>
                            {bid.driver?.driverRating && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Rating value={bid.driver.driverRating} readOnly precision={0.1} size="small" />
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.5em' }}>
                                  ⭐ {bid.driver.driverRating.toFixed(1)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ textAlign: 'right', minWidth: '120px' }}>
                            <Typography variant="h5" color="secondary.main" sx={{ fontWeight: 'bold', fontSize: '1.875rem' }}>
                              ${bid.priceOffer} CAD
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.5em' }}>
                              Offered Price
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: '150px' }}>
                            {bid.status === 'PENDING' ? (
                              <Button
                                variant="contained"
                                color="success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAcceptBid(bid.id);
                                }}
                                sx={{ fontSize: '1.5em', py: 1, px: 2, fontWeight: 'bold' }}
                              >
                                ✓ Accept
                              </Button>
                            ) : bid.status === 'ACCEPTED' ? (
                              <Chip
                                label="✓ Accepted"
                                color="success"
                                sx={{ fontSize: '1.5em', fontWeight: 'bold', py: 2 }}
                              />
                            ) : null}
                          </Box>
                        </Box>
                        {bid.message && (
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', gap: 1, fontSize: '1.5em' }}>
                              <strong>Message:</strong>
                              <TranslatingText text={bid.message} variant="body2" sx={{ fontSize: 'inherit' }} />
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.5em' }}>
                  {t('myRides.cards.noBids', 'No bids found')}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setBidsDialogOpen(false);
              setSelectedRequestBids([]);
            }} sx={{ fontSize: '1.5em' }}>
              {t('common.close', 'Close')}
            </Button>
          </DialogActions>
        </Dialog>

        <SocialShareModal
          open={!!shareItem}
          onClose={() => setShareItem(null)}
          type={shareItem?.type || 'ride'}
          data={shareItem?.data || {}}
        />
      </Container >
    </Box >
  );
};

export default MyRidesPage;